import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import {
  GoogleMap,
  useLoadScript,
  Marker,
  OverlayView,
  MarkerClusterer,
  Circle,
} from "@react-google-maps/api";
import { useLocation, useNavigate } from "react-router-dom";
import DashboardHeader from "../../components/header/DashboardHeader";
import Pagination from "../../components/pagination/Pagination";
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import {
  Grid,
  styled,
  Table,
  TableBody,
  TableCell,
  tableCellClasses,
  TableHead,
  TableRow,
  Box,
  Button,
  Typography,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  OutlinedInput,
} from "@mui/material";
import CloseIcon from '@mui/icons-material/Close';
import CircularProgress from "@mui/material/CircularProgress";
import config from "../../config.js";
import "./stops.css";
import NavMenu from "../../components/navbar/NavMenu";
import TitleBar from "../../components/navbar/TitleBar";
import { useTranslation } from "react-i18next";
import SubTitle from "../../components/common/SubTitle";
import MonitorSubtitle from "../../components/common/MonitorSubtitle";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { format, isValid } from "date-fns";
import { getUserDateFormat, convertToDateFnsFormat } from "../../utils/formatDate";
import Skeleton from "@mui/material/Skeleton";
import {
  getAllRoutes,
  getAllStops,
  getAllVehicles,
  getVehicleDirection,
} from "../../components/dashboard/dashboardAPI.js";
import { getOrganizationId, reportsApi } from "../../components/manageData/reportsApi.js";
import Footer from "../../components/common/Footer.jsx";
import { useSettings } from "../../context/SettingsContext";
import { useMapIcons } from "../../hooks/useMapIcons.js";
import {
  computeVehicleLabelOffsets,
  computeStopLabelOffsets,
} from "../../utils/mapLabelOffsets.js";
import { formatISODateIST } from "../../utils/istDateUtils.js";
import {
  createAnalyticsDateChunks,
  mergeReportResponses,
  validateAnalyticsDateRange,
} from "../../utils/analyticsDateRange.js";
import BarGraph from "../../components/manageData/Analytics/charts/BarGraph.jsx";
import PieChart from "../../components/manageData/Analytics/charts/PieChart.jsx";
import StopHeaderDeviceItems from "../../components/stops/StopConnectedDevices.jsx";

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: "#647490",
    color: "white",
    fontSize: 20,
    fontWeight: 600,
    width: "20%",
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 24,
  },
}));

const colors = [
  "#20A8E0",
  "#8FBC8F",
  "#FFD700",
  "#6495ED",
  "#FF69B4",
  "#FFA07A",
  "#ADFF2F",
  "#FF4500",
  "#9370DB",
  "#FFFFFF",
];

const libraries = ["places", "directions", "geometry"];

const LoadingScreen = ({
  t,
  navigate,
  selectedStopData,
  localStopsData,
  isSchoolMonitor = false,
}) => {
  const handleViewStop = () => {
    navigate(`/track/screen/${selectedStopData.id}`, {
      state: { selectedStopData, localStopsData },
    });
  };

  const handleStopSchedules = () => {
    navigate("/stop/Schedule/Details", {
      state: { id: selectedStopData.id },
    });
  };

  return (
    <>
      {!isSchoolMonitor && (
        <>
          <TitleBar />
          <NavMenu />
        </>
      )}
      <MonitorSubtitle
        title={t(isSchoolMonitor ? "Monitor School Stop" : "Monitor Stop")}
        path={t("Stops > Monitor Stop")}
        track={isSchoolMonitor ? null : "Stop View"}
        add={isSchoolMonitor ? null : "View Schedules"}
        onTrackClick={isSchoolMonitor ? undefined : handleViewStop}
        onAddClick={isSchoolMonitor ? undefined : handleStopSchedules}
      />

      <Box
        sx={{
          padding: { xs: "0.9375rem", sm: "1.25rem", md: "1.875rem" },
          margin: { xs: "0 0.9375rem", sm: "0 1.25rem", md: "0 2.5rem" },
          display: "flex",
          flexDirection: "column",
          gap: "1.25rem",
        }}
      >
        {/* Top Info Section Skeleton */}
        <Box
          sx={{
            borderRadius: "0.625rem",
            border: "1px solid #A5DCF3",
            backgroundColor: "#FFFFFF",
            boxShadow: "-2px 1px 5px 1px #00000040",
            padding: "1.25rem",
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            justifyContent: "space-between",
            gap: "1.25rem",
          }}
        >
          {[...Array(4)].map((_, index) => (
            <React.Fragment key={index}>
              <Box sx={{ flex: 1, textAlign: "center" }}>
                <Skeleton
                  variant="text"
                  width="60%"
                  height="1.5rem"
                  sx={{ mx: "auto", mb: "0.5rem" }}
                />
                <Skeleton
                  variant="text"
                  width="80%"
                  height="2rem"
                  sx={{ mx: "auto" }}
                />
              </Box>
              {index < 3 && (
                <Skeleton
                  variant="rectangular"
                  sx={{
                    alignSelf: "center",
                    width: { xs: "100%", sm: "1px" },
                    height: { xs: "1px", sm: "3.75rem" },
                  }}
                />
              )}
            </React.Fragment>
          ))}
        </Box>

        {/* Map Section Skeleton */}
        <Box
          sx={{
            borderRadius: "0.625rem",
            border: "1px solid #A5DCF3",
            backgroundColor: "#FFFFFF",
            boxShadow: "-2px 1px 5px 1px #00000040",
            padding: "1.25rem",
            margin: {
              xs: "0.625rem 0.9375rem",
              sm: "0.625rem 1.25rem",
              md: "1.25rem 2.5rem",
            },
          }}
        >
          <Skeleton
            variant="rectangular"
            width="100%"
            height="34.375rem"
            sx={{ borderRadius: "1.25rem" }}
          />
        </Box>

        {/* Table Section Skeleton */}
        <Box
          sx={{
            borderRadius: "0.625rem", // Converted 10px to rem
            border: "1px solid #A5DCF3",
            backgroundColor: "#FFFFFF",
            boxShadow: "-2px 1px 5px 1px #00000040",
            padding: "1.25rem", // Converted 20px to rem
            margin: {
              xs: "0.625rem 0.9375rem",
              sm: "0.625rem 1.25rem",
              md: "0.625rem 2.5rem",
            }, // Converted 10px, 15px, 20px, 40px to rem
          }}
        >
          <Skeleton
            variant="rectangular"
            width="100%"
            height="3rem"
            sx={{ mb: "0.5rem" }}
          />{" "}
          {/* Table header */}
          {[...Array(3)].map((_, index) => (
            <Skeleton
              key={index}
              variant="rectangular"
              width="100%"
              height="2rem"
              sx={{ mb: "0.5rem" }}
            /> // Table rows
          ))}
        </Box>
      </Box>
    </>
  );
};

const normalizeId = (value) =>
  value === undefined || value === null ? "" : String(value).trim();

const normalizeLabel = (value) => normalizeId(value) || "Unknown";

const isActiveVehicleStatus = (status) => {
  const normalized = String(status || "").trim().toLowerCase();
  if (!normalized) return false;
  if (normalized === "active" || normalized === "running") return true;
  if (/\bnot\s+running\b/.test(normalized) || normalized.includes("inactive")) {
    return false;
  }
  return /\brunning\b/.test(normalized);
};

const getReportVehicleKey = (row, fallback) =>
  normalizeId(
    row?.vehicleId ||
      row?.vehicle_id ||
      row?.deviceId ||
      row?.device_id ||
      row?.imei ||
      row?.IMEI ||
      row?.uniqueId ||
      row?.vehicleNumber ||
      row?.registrationNumber ||
      row?.registration_number ||
      fallback,
  );

const getStopVisitCountFromRuntimeRow = (row, stopData) => {
  const selectedStopIds = new Set(
    [stopData?.id, stopData?.stopId].map(normalizeId).filter(Boolean),
  );
  const selectedStopName = normalizeId(stopData?.name).toLowerCase();
  const stopDetails = Array.isArray(row?.stopDetails) ? row.stopDetails : [];

  if (stopDetails.length) {
    return stopDetails.filter((detail) => {
      const detailStopIds = [
        detail?.id,
        detail?.stopId,
        detail?.stop_id,
        detail?.stopCode,
      ].map(normalizeId);
      const idMatches = detailStopIds.some((id) => selectedStopIds.has(id));
      const nameMatches =
        selectedStopName &&
        normalizeId(detail?.stopName || detail?.name).toLowerCase() ===
        selectedStopName;

      return idMatches || nameMatches;
    }).length;
  }

  const rowStopIds = [row?.id, row?.stopId, row?.stop_id].map(normalizeId);
  const rowMatchesStop =
    rowStopIds.some((id) => selectedStopIds.has(id)) ||
    (selectedStopName &&
      normalizeId(row?.stopName || row?.name).toLowerCase() === selectedStopName);

  if (!rowMatchesStop) return 0;

  return Number(
    row?.visits ??
    row?.visitCount ??
    row?.visitedCount ??
    row?.count ??
    row?.noOfStopsCovered ??
    0,
  );
};

const getRuntimeVehicleLabel = (row, fallback) =>
  row?.vehicleName ||
  row?.deviceName ||
  row?.vehicleNumber ||
  row?.deviceId ||
  fallback ||
  "Vehicle";

const isRuntimeRowInactive = (row) => {
  const status = normalizeId(
    row?.deadStatus || row?.vehicleStatus || row?.status || row?.state,
  ).toLowerCase();

  return status.includes("inactive") || status.includes("dead");
};

const parseReportDateTime = (value) => {
  if (!value) return null;
  if (value instanceof Date) return value;

  const match = String(value)
    .trim()
    .match(/^(\d{1,2})\/(\d{1,2})\/(\d{4}),?\s+(\d{1,2}):(\d{2})(?::(\d{2}))?\s*(am|pm)?$/i);

  if (match) {
    const [, day, month, year, hourValue, minute, second = "0", meridiem] = match;
    let hour = Number(hourValue);
    if (meridiem?.toLowerCase() === "pm" && hour < 12) hour += 12;
    if (meridiem?.toLowerCase() === "am" && hour === 12) hour = 0;

    const date = new Date(
      Number(year),
      Number(month) - 1,
      Number(day),
      hour,
      Number(minute),
      Number(second),
    );

    return Number.isNaN(date.getTime()) ? null : date;
  }

  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const getVisitDurationMinutes = (row) => {
  const entryTime = parseReportDateTime(row?.entryTime);
  const exitTime = parseReportDateTime(row?.exitTime);
  if (!entryTime || !exitTime) return null;

  const duration = (exitTime.getTime() - entryTime.getTime()) / 60000;
  return duration >= 0 ? duration : null;
};

const getMinutesFromDayStart = (date) =>
  date.getHours() * 60 + date.getMinutes() + date.getSeconds() / 60;

const formatTimelineMinutes = (value) => {
  const totalMinutes = Math.max(0, Math.round(value)) % 1440;
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}`;
};

const parseISODate = (value) => {
  if (!value) return null;
  const [year, month, day] = String(value).split("-").map(Number);
  if (!year || !month || !day) return null;
  return new Date(year, month - 1, day);
};

const getTimelineRange = (dateOption, customDateRange) => {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  let startDate = new Date(today);
  let endDate = new Date(today);
  endDate.setHours(23, 59, 59, 999);

  if (dateOption === "yesterday") {
    startDate.setDate(startDate.getDate() - 1);
    endDate = new Date(startDate);
    endDate.setHours(23, 59, 59, 999);
  } else if (dateOption === "last 7 days") {
    startDate.setDate(startDate.getDate() - 6);
    endDate = new Date(today);
    endDate.setHours(23, 59, 59, 999);
  } else if (dateOption === "custom" && customDateRange?.startDate && customDateRange?.endDate) {
    const customStart = parseISODate(customDateRange.startDate);
    const customEnd = parseISODate(customDateRange.endDate);
    if (customStart && customEnd) {
      startDate = customStart;
      endDate = new Date(customEnd);
      endDate.setHours(23, 59, 59, 999);
    }
  }

  const rangeMinutes = Math.max(
    1440,
    Math.ceil((endDate.getTime() - startDate.getTime()) / 60000),
  );

  return { startDate, endDate, rangeMinutes };
};

const getEntryExitTimelineItem = (
  row,
  index,
  rangeStartDate = null,
  rangeEndDate = null,
) => {
  const entryTime = parseReportDateTime(row?.entryTime);
  const exitTime = parseReportDateTime(row?.exitTime);
  const duration = getVisitDurationMinutes(row);
  if (!entryTime || !exitTime || duration === null) return null;

  if (rangeStartDate && rangeEndDate) {
    const clippedStartTime = new Date(
      Math.max(entryTime.getTime(), rangeStartDate.getTime()),
    );
    const clippedEndTime = new Date(
      Math.min(exitTime.getTime(), rangeEndDate.getTime()),
    );

    if (clippedEndTime <= clippedStartTime) return null;

    const vehicleId = getReportVehicleKey(row, row?.id || index) || null;
    const vehicleLabel =
      row.vehicleName || row.vehicleNumber || row.deviceName || normalizeLabel(vehicleId) || "Unknown";
    const start = Math.max(
      0,
      Math.round((clippedStartTime.getTime() - rangeStartDate.getTime()) / 60000),
    );
    const clippedDuration =
      (clippedEndTime.getTime() - clippedStartTime.getTime()) / 60000;

    return {
      vehicleId,
      vehicleKey: vehicleId || vehicleLabel,
      label: vehicleLabel,
      vehicle: vehicleLabel,
      start,
      duration: Number(clippedDuration.toFixed(1)),
      range: [start, start + clippedDuration],
      entryLabel: row.entryTime || formatTimelineMinutes(start),
      exitLabel: row.exitTime || formatTimelineMinutes(start + clippedDuration),
    };
  }

  const vehicleId = getReportVehicleKey(row, row?.id || index) || null;
  const vehicleLabel =
    row.vehicleName || row.vehicleNumber || row.deviceName || normalizeLabel(vehicleId) || "Unknown";
  const vehicleKey = vehicleId || vehicleLabel;
  const rawStart = rangeStartDate
    ? Math.round((entryTime.getTime() - rangeStartDate.getTime()) / 60000)
    : getMinutesFromDayStart(entryTime);
  const start = Math.max(0, rawStart);

  return {
    vehicleId,
    vehicleKey,
    label: vehicleLabel,
    vehicle: vehicleLabel,
    start,
    duration: Number(duration.toFixed(1)),
    range: [start, start + duration],
    entryLabel: row.entryTime || formatTimelineMinutes(start),
    exitLabel: row.exitTime || formatTimelineMinutes(start + duration),
  };
};

const groupTimelineVisitsByVehicle = (timelineVisits) => {
  const grouped = timelineVisits.reduce((acc, item) => {
    const key = normalizeId(item.vehicleId) || normalizeId(item.vehicleKey) || normalizeId(item.vehicle) || normalizeId(item.label) || "Unknown";
    const label = normalizeLabel(item.label || item.vehicle || item.vehicleKey);
    if (!acc[key]) {
      acc[key] = { vehicleKey: key, label, vehicle: label, segments: [] };
    }
    acc[key].segments.push(item);
    return acc;
  }, {});

  return Object.values(grouped).map((group) => {
    const sortedSegments = group.segments.sort((a, b) => a.start - b.start);
    const row = {
      vehicleKey: group.vehicleKey,
      label: group.label,
      vehicle: group.vehicle,
      segmentCount: sortedSegments.length,
      segments: sortedSegments,
    };

    sortedSegments.forEach((segment, idx) => {
      row[`range_${idx}`] = segment.range;
      row[`entryLabel_${idx}`] = segment.entryLabel;
      row[`exitLabel_${idx}`] = segment.exitLabel;
      row[`duration_${idx}`] = segment.duration;
    });

    return row;
  }).sort((a, b) => a.label.localeCompare(b.label));
};

const EntryExitTimelineChart = ({ data = [], title, t, dateOption, customDateRange }) => {
  const [hoveredSegment, setHoveredSegment] = useState(null);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const { startDate: rangeStartDate, rangeMinutes } = getTimelineRange(
    dateOption,
    customDateRange,
  );
  const isMultiDay = rangeMinutes > 1440;

  const formatXAxisLabel = (tickItem) => {
    if (isMultiDay) {
      const tickDate = new Date(rangeStartDate.getTime() + tickItem * 60000);
      return tickDate.toLocaleDateString(undefined, {
        weekday: "short",
        month: "short",
        day: "numeric",
      });
    }

    const hours = Math.floor(tickItem / 60);
    const minutes = tickItem % 60;
    return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}`;
  };

  const handleMouseMove = (segment, vehicleName, event) => {
    const svg = event.currentTarget.closest("svg");
    if (!svg) return;
    const rect = svg.getBoundingClientRect();
    setHoveredSegment({ ...segment, vehicleName });
    setMousePosition({
      x: event.clientX - rect.left,
      y: event.clientY - rect.top,
    });
  };

  const handleMouseLeave = () => {
    setHoveredSegment(null);
  };

  const VehicleTimelineBar = ({ payload, x, y, width, height }) => {
    const trips = payload?.trips || [];
    const totalMinutes = rangeMinutes;

    return (
      <g>
        {trips.map((trip, index) => {
          const tripStartX = x + (trip.start / totalMinutes) * width;
          const tripWidth = Math.max((trip.duration / totalMinutes) * width, 2);
          const fillColor = trip.color || "#16A085";

          return (
            <rect
              key={index}
              x={tripStartX}
              y={y}
              width={tripWidth}
              height={height}
              fill={fillColor}
              stroke="#FFFFFF"
              strokeWidth={1}
              style={{ cursor: "pointer" }}
              onMouseMove={(event) => handleMouseMove(trip, payload.vehicleName, event)}
              onMouseLeave={handleMouseLeave}
            />
          );
        })}
      </g>
    );
  };

  const chartData = data.map((item) => ({
    vehicleName: item.label,
    totalDuration: rangeMinutes,
    trips: item.segments || [],
  }));

  return (
    <Box sx={{ width: "100%", height: "100%", position: "relative" }}>
      <Typography
        variant="h6"
        sx={{
          textAlign: "center",
          mb: 0.5,
          color: "#183059",
          fontWeight: 600,
          fontSize: "16px",
        }}
      >
        {title}
      </Typography>
      <ResponsiveContainer width="100%" height={320}>
        <BarChart
          data={chartData}
          layout="vertical"
          margin={{ top: 20, right: 30, left: 20, bottom: 20 }}
          barCategoryGap={10}
        >
          <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
          <XAxis
            type="number"
            domain={[0, rangeMinutes]}
            tickFormatter={formatXAxisLabel}
            ticks={
              isMultiDay
                ? Array.from(
                    { length: Math.floor(rangeMinutes / 1440) + 1 },
                    (_, index) => index * 1440,
                  )
                : [0, 240, 480, 720, 960, 1200, 1440]
            }
            tick={{ fontSize: 10, fill: "#666" }}
            axisLine={{ stroke: "#e0e0e0" }}
          />
          <YAxis
            type="category"
            dataKey="vehicleName"
            width={140}
            tick={{ fontSize: 12, fill: "#666" }}
            axisLine={{ stroke: "#e0e0e0" }}
          />
          <Bar
            dataKey="totalDuration"
            fill="transparent"
            shape={<VehicleTimelineBar />}
            barSize={30}
          />
        </BarChart>
      </ResponsiveContainer>

      {hoveredSegment && (
        <Box
          sx={{
            position: "absolute",
            left: mousePosition.x + 12,
            top: mousePosition.y - 60,
            backgroundColor: "white",
            border: "1px solid #ccc",
            borderRadius: "4px",
            padding: "10px",
            boxShadow: "0 2px 4px rgba(0,0,0,0.12)",
            zIndex: 1000,
            pointerEvents: "auto",
            minWidth: "180px",
          }}
        >
          <Box sx={{ position: "absolute", top: 6, right: 6, cursor: "pointer" }} onClick={() => setHoveredSegment(null)}>
            <CloseIcon sx={{ fontSize: 16, color: "#666" }} />
          </Box>
          <Typography sx={{ fontWeight: 700, fontSize: "12px", mb: 0.5, pt: 1 }}>
            {hoveredSegment.vehicleName || hoveredSegment.vehicle}
          </Typography>
          <Typography sx={{ fontSize: "12px", mb: 0.5 }}>
            {t("Entry")}: {hoveredSegment.entryLabel}
          </Typography>
          <Typography sx={{ fontSize: "12px", mb: 0.5 }}>
            {t("Exit")}: {hoveredSegment.exitLabel}
          </Typography>
          <Typography sx={{ fontSize: "12px" }}>
            {t("Duration")}: {hoveredSegment.duration} {t("min")}
          </Typography>
        </Box>
      )}
    </Box>
  );
};

const ChartCardSkeleton = ({ variant = "bar" }) => (
  <Box
    sx={{
      width: "100%",
      height: "100%",
      minHeight: 260,
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      gap: 2,
    }}
  >
    <Skeleton width="50%" height={24} />
    {variant === "pie" ? (
      <Skeleton variant="circular" width={150} height={150} />
    ) : (
      <Box sx={{ display: "flex", alignItems: "flex-end", gap: 1, height: 150 }}>
        {[80, 120, 96, 140, 110].map((height, index) => (
          <Skeleton
            key={index}
            variant="rectangular"
            width={28}
            height={height}
            sx={{ borderRadius: "4px 4px 0 0" }}
          />
        ))}
      </Box>
    )}
    <Box sx={{ display: "flex", gap: 1 }}>
      <Skeleton width={64} height={16} />
      <Skeleton width={64} height={16} />
    </Box>
  </Box>
);

const TimelineChartSkeleton = () => (
  <Box sx={{ width: "100%", minHeight: 300, p: 2 }}>
    <Skeleton width="30%" height={24} sx={{ mx: "auto", mb: 3 }} />
    {[0, 1, 2, 3, 4].map((item) => (
      <Box key={item} sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
        <Skeleton width={120} height={18} />
        <Skeleton variant="rectangular" height={26} sx={{ flex: 1, borderRadius: 1 }} />
      </Box>
    ))}
  </Box>
);

const ChunkLoadingIndicator = ({ progress, t }) => {
  if (!progress || progress.percentComplete >= 100) return null;

  return (
    <Box
      sx={{
        position: "absolute",
        top: 8,
        right: 8,
        zIndex: 2,
      }}
    >
      <Box
        sx={{
          display: "inline-flex",
          alignItems: "center",
          gap: 0.75,
          backgroundColor: "rgba(255, 255, 255, 0.95)",
          border: "1px solid #D7E3F0",
          borderRadius: "999px",
          boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
          color: "#5F6B7A",
          fontSize: 12,
          fontWeight: 600,
          px: 1.25,
          py: 0.5,
        }}
      >
        <CircularProgress size={14} thickness={5} />
        <span>{progress.percentComplete}%</span>
      </Box>
    </Box>
  );
};

const StopAnalyticsPanel = ({
  stopAnalytics,
  entryExitAnalytics,
  stopChunkProgress,
  entryExitChunkProgress,
  dateOption,
  customDateRange,
  appliedCustomDateRange,
  onDateOptionChange,
  onCustomDateRangeChange,
  onApplyCustomDateRange,
  dateError,
  t,
}) => {
  const activeInactiveData = stopAnalytics?.activeInactive || [];
  const userDateFormat = getUserDateFormat();
  const dateInputFormat = convertToDateFnsFormat(userDateFormat);
  const parseDate = (d) => {
    if (!d) return null;
    if (d instanceof Date) return isValid(d) ? d : null;
    const parsed = new Date(`${d}T00:00:00`);
    return isValid(parsed) ? parsed : null;
  };

  const chartCardSx = {
    border: "1px solid #A5DCF3",
    borderRadius: "10px",
    padding: "16px",
    backgroundColor: "#FFFFFF",
    boxShadow: "-2px 1px 5px 1px #00000024",
    minHeight: "350px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  };

  return (
    <Box
      sx={{
        borderRadius: "10px",
        padding: { xs: "14px", sm: "18px", md: "16px" },
        backgroundColor: "#FFFFFF",
       border: "1px solid #A5DCF3",
        boxShadow: "-2px 1px 5px 1px #00000040",
        margin: { xs: "10px 40px", sm: "15px 40px", md: "10px 40px" },
      }}
    >
      <Box sx={{ mb: 2 }}>
        <Typography
          sx={{
            background: "#647490",
            color: "#FFFFFF",
            fontFamily: "Roboto",
            fontWeight: 700,
            fontSize: { xs: "18px", md: "20px" },
            borderRadius: "8px",
            padding: "16px",
            boxShadow: "-2px 2px 5px 0px #00000040",
            width: "100%",
          }}
        >
          {t("Stop Analytics")}
        </Typography>
        <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}>
          <FormControl
            size="small"
            sx={{
              minWidth: 180,
              backgroundColor: "#FFFFFF",
              borderRadius: "12px",
              height: 43,
            }}
          >
            <Select
              id="stop-analytics-duration"
              value={dateOption}
              onChange={(event) => onDateOptionChange(event.target.value)}
              input={
                <OutlinedInput
                  notched={false}
                  sx={{
                    borderRadius: "12px",
                    border: "none",
                    height: 43,
                    px: 0,
                     "& fieldset": { border: "none" },
                  }}
                />
              }
              displayEmpty
              renderValue={(selected) => {
                if (!selected) {
                  return t("Today");
                }
                switch (selected) {
                  case "today":
                    return t("Today");
                  case "yesterday":
                    return t("Yesterday");
                  case "last 7 days":
                    return t("This Week");
                  case "custom":
                    return t("Custom");
                  default:
                    return selected;
                }
              }}
              sx={{
                borderRadius: "12px",
                minWidth: 180,
                height: 43,
                fontSize: "0.95rem",
                color: "#1F2937",
                backgroundColor: "#FFFFFF",
                border: "1px solid #183059",
                boxSizing: "border-box",
                "& .MuiOutlinedInput-notchedOutline": { border: "none" },
                "& fieldset": { border: "none" },
                ".MuiSelect-select": {
                  paddingTop: "12px",
                  paddingRight: "24px",
                  paddingBottom: "12px",
                  paddingLeft: "24px",
                  height: 43,
                  display: "flex",
                  alignItems: "center",
                },
              }}
              MenuProps={{
                PaperProps: {
                  sx: {
                    borderRadius: "12px",
                    mt: 1,
                    minWidth: 180,
                  },
                },
              }}
            >
              <MenuItem value="today">{t("Today")}</MenuItem>
              <MenuItem value="yesterday">{t("Yesterday")}</MenuItem>
              <MenuItem value="last 7 days">{t("This Week")}</MenuItem>
              <MenuItem value="custom">{t("Custom")}</MenuItem>
            </Select>
          </FormControl>
        </Box>
      </Box>
      {dateOption === "custom" && (
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              justifyContent: "flex-end",
              alignItems: { xs: "stretch", sm: "flex-start" },
              gap: 1,
              mb: 2,
            }}
          >
            <DatePicker
              label={t("Start Date")}
              value={parseDate(customDateRange.startDate)}
              maxDate={parseDate(customDateRange.endDate) || undefined}
              onChange={(newDate) => {
                const val = newDate && isValid(newDate) ? format(newDate, "yyyy-MM-dd") : "";
                onCustomDateRangeChange({
                  ...customDateRange,
                  startDate: val,
                });
              }}
              format={dateInputFormat}
              slotProps={{
                textField: {
                  size: "small",
                  error: !!dateError,
                  placeholder: userDateFormat,
                  InputLabelProps: { shrink: true },
                  sx: { backgroundColor: "#fff" },
                },
              }}
            />
            <DatePicker
              label={t("End Date")}
              value={parseDate(customDateRange.endDate)}
              minDate={parseDate(customDateRange.startDate) || undefined}
              onChange={(newDate) => {
                const val = newDate && isValid(newDate) ? format(newDate, "yyyy-MM-dd") : "";
                onCustomDateRangeChange({
                  ...customDateRange,
                  endDate: val,
                });
              }}
              format={dateInputFormat}
              slotProps={{
                textField: {
                  size: "small",
                  error: !!dateError,
                  helperText: dateError || " ",
                  placeholder: userDateFormat,
                  InputLabelProps: { shrink: true },
                  sx: { backgroundColor: "#fff" },
                },
              }}
            />
            <Button
              variant="contained"
              onClick={onApplyCustomDateRange}
              sx={{
                minWidth: 110,
                height: 40,
                backgroundColor: "#20A8E0",
                textTransform: "none",
                fontWeight: 600,
                "&:hover": { backgroundColor: "#168FC2" },
              }}
            >
              {t("Apply")}
            </Button>
          </Box>
        </LocalizationProvider>
      )}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "1fr",
            md: "repeat(3, minmax(0, 1fr))",
          },
          gap: 3,
        }}
      >
        <Box sx={{ ...chartCardSx, position: "relative" }}>
          <ChunkLoadingIndicator progress={stopChunkProgress} t={t} />
          {stopAnalytics?.loading ? (
            <ChartCardSkeleton variant="pie" />
          ) : stopAnalytics?.error ? (
            <Typography color="error">{stopAnalytics.error}</Typography>
          ) : (
            <PieChart
              data={activeInactiveData}
              title={t("Active / Inactive Vehicles")}
              xAxis="status"
              yAxis="count"
              height={230}
              width={360}
            />
          )}
        </Box>
        <Box sx={{ ...chartCardSx, position: "relative" }}>
          <ChunkLoadingIndicator progress={entryExitChunkProgress} t={t} />
          {entryExitAnalytics?.loading ? (
            <ChartCardSkeleton />
          ) : entryExitAnalytics?.error ? (
            <Typography color="error">{entryExitAnalytics.error}</Typography>
          ) : entryExitAnalytics?.vehicleVisits?.length > 0 ? (
            <BarGraph
              data={entryExitAnalytics.vehicleVisits}
              title={t("Vehicle Visited")}
              xAxis="vehicle"
              yAxis="visits"
              xAxisLabel={t("Vehicles")}
              yAxisLabel={t("Events")}
              color="#E67E22"
              height={260}
              barSize={14}
            />
          ) : (
            <Typography sx={{ color: "#183059", fontWeight: 600 }}>
              {t("No entry/exit events for this stop")}
            </Typography>
          )}
        </Box>
        <Box sx={{ ...chartCardSx, position: "relative" }}>
          <ChunkLoadingIndicator progress={entryExitChunkProgress} t={t} />
          {entryExitAnalytics?.loading ? (
            <ChartCardSkeleton />
          ) : entryExitAnalytics?.error ? (
            <Typography color="error">{entryExitAnalytics.error}</Typography>
          ) : entryExitAnalytics?.visitDurations?.length > 0 ? (
            <BarGraph
              data={entryExitAnalytics.visitDurations}
              title={t("Average Visit Duration")}
              xAxis="vehicle"
              yAxis="duration"
              xAxisLabel={t("Vehicles")}
              yAxisLabel={t("Minutes")}
              color="#20A8E0"
              height={260}
              barSize={14}
            />
          ) : (
            <Typography sx={{ color: "#183059", fontWeight: 600 }}>
              {t("No entry/exit duration data")}
            </Typography>
          )}
        </Box>
        <Box sx={{ ...chartCardSx, position: "relative", gridColumn: { xs: "1", md: "1 / -1" } }}>
          <ChunkLoadingIndicator progress={entryExitChunkProgress} t={t} />
          {entryExitAnalytics?.loading ? (
            <TimelineChartSkeleton />
          ) : entryExitAnalytics?.error ? (
            <Typography color="error">{entryExitAnalytics.error}</Typography>
          ) : entryExitAnalytics?.timelineVisits?.length > 0 ? (
            <EntryExitTimelineChart
              data={entryExitAnalytics.timelineVisits}
              title={t("Entry / Exit Timeline")}
              t={t}
              dateOption={dateOption}
              customDateRange={appliedCustomDateRange || customDateRange}
            />
          ) : (
            <Typography sx={{ color: "#183059", fontWeight: 600 }}>
              {t("No entry/exit timeline data")}
            </Typography>
          )}
        </Box>
      </Box>
    </Box>
  );
};

const AdminStopView = ({
  selectedStopData: providedSelectedStopData,
  localStopsData: providedLocalStopsData,
  isSchoolMonitor = false,
}) => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const queryParams = new URLSearchParams(location.search);
  const schoolMonitor =
    isSchoolMonitor || queryParams.get("schoolMonitor") === "true";
  const queryStopId = queryParams.get("stopId");
  const { selectedStopData: stateSelectedStopData, localStopsData: stateLocalStopsData } =
    location.state || {};
  const storedSelectedStopData = useMemo(() => {
    if (!queryStopId) return null;

    try {
      const storedStop = localStorage.getItem(`schoolStopMonitor:${queryStopId}`);
      return storedStop ? JSON.parse(storedStop) : null;
    } catch (error) {
      console.error("Error reading stored stop monitor data:", error);
      return null;
    }
  }, [queryStopId]);
  const storedLocalStopsData = useMemo(() => {
    try {
      const storedStops = localStorage.getItem("schoolStopMonitor:allStops");
      return storedStops ? JSON.parse(storedStops) : null;
    } catch (error) {
      console.error("Error reading stored stops monitor data:", error);
      return null;
    }
  }, []);
  const selectedStopData =
    providedSelectedStopData || stateSelectedStopData || storedSelectedStopData;
  const localStopsData =
    providedLocalStopsData || stateLocalStopsData || storedLocalStopsData;

  const copyOpenerAuthToSession = useCallback(() => {
    if (typeof window === "undefined") {
      return;
    }
    if (
      window.sessionStorage.getItem("authToken") ||
      !window.opener ||
      window.opener === window
    ) {
      return;
    }

    try {
      const openerSession = window.opener.sessionStorage;
      const openerAuthToken = openerSession.getItem("authToken");
      const openerRefreshToken = openerSession.getItem("refreshToken");
      if (openerAuthToken) {
        window.sessionStorage.setItem("authToken", openerAuthToken);
      }
      if (openerRefreshToken) {
        window.sessionStorage.setItem("refreshToken", openerRefreshToken);
      }
    } catch (error) {
      console.warn(
        "[SchoolStopMonitor] failed to copy auth tokens from opener:",
        error,
      );
    }
  }, []);

  useEffect(() => {
    copyOpenerAuthToSession();
  }, [copyOpenerAuthToSession]);

  const [stopData, setStopData] = useState(null);
  const [allStopData, setAllStopData] = useState(null);
  const [isInitialized, setIsInitialized] = useState(false);
  const [isInitialVehicleLoadComplete, setIsInitialVehicleLoadComplete] =
    useState(false);
  const [directions, setDirections] = useState([]);
  const [vehicleData, setVehicleData] = useState({});
  const [error, setError] = useState(null);
  const routePathsRef = useRef({});
  const [map, setMap] = useState(null);
  const [isLargeScreen, setIsLargeScreen] = useState(window.innerWidth > 768);
  const [vehicleHistory, setVehicleHistory] = useState({});
  const [stoppedVehicles, setStoppedVehicles] = useState({});
  const [stopAnalytics, setStopAnalytics] = useState({
    stopsVisited: [],
    totalStopsVisited: 0,
    activeInactive: [],
    loading: false,
    error: null,
  });
  const [entryExitAnalytics, setEntryExitAnalytics] = useState({
    vehicleVisits: [],
    visitDurations: [],
    timelineVisits: [],
    loading: false,
    error: null,
  });
  const [stopChunkProgress, setStopChunkProgress] = useState(null);
  const [entryExitChunkProgress, setEntryExitChunkProgress] = useState(null);
  const [entryExitDateOption, setEntryExitDateOption] = useState("today");
  const [entryExitCustomRange, setEntryExitCustomRange] = useState({
    startDate: formatISODateIST(new Date()),
    endDate: formatISODateIST(new Date()),
  });
  const [draftEntryExitCustomRange, setDraftEntryExitCustomRange] = useState({
    startDate: formatISODateIST(new Date()),
    endDate: formatISODateIST(new Date()),
  });
  const [entryExitDateError, setEntryExitDateError] = useState("");
  const vehicleHistoryRef = useRef({});
  const stoppedVehiclesRef = useRef({});
  const stopDataRef = useRef(null);
  const handleSSEMessageRef = useRef(null);
  const [vehicleIds, setVehicleIds] = useState([]);
  const [userState, setUserState] = useState(null);
  const [hasMapPermission, setHasMapPermission] = useState(false);
  const eventSourceRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const isConnectedRef = useRef(false);
  const [filteredStops, setFilteredStops] = useState([]);
  const [filteredRoutes, setFilteredRoutes] = useState([]);
  const [filteredVehicles, setFilteredVehicles] = useState([]);
  const [distanceWithStop, setDistanceWithStop] = useState([]);
  const [vehiclesAtStop, setVehiclesAtStop] = useState(0);
  const [zoom, setZoom] = useState(null);
  const [vehicleDirections, setVehicleDirections] = useState({});
  const [hoveredVehicle, setHoveredVehicle] = useState(null);
  const [vehicleStatuses, setVehicleStatuses] = useState({});
  const { isLoaded, loadError } = useLoadScript({
    googleMapsApiKey: config.googleMapsApiKey,
    libraries,
  });

  const { settings } = useSettings();
  const { getIntermediateStopIcon, getVehicleIcon: getCustomVehicleIcon } =
    useMapIcons(settings, filteredVehicles, filteredStops, filteredRoutes);

  const getVehicleTrackingId = useCallback((vehicle) => {
    if (!vehicle || typeof vehicle !== "object") return null;

    return normalizeId(
      vehicle.device_id ||
      vehicle.deviceId ||
      vehicle.deviceID ||
      vehicle.DeviceId ||
      vehicle.imei ||
      vehicle.IMEI ||
      vehicle.uniqueId ||
      vehicle.UniqueId ||
      null
    );
  }, []);

  const getVehicleCountId = useCallback(
    (vehicle, fallbackId) => {
      if (vehicle == null) return null;
      if (typeof vehicle !== "object") return normalizeId(vehicle);

      return normalizeId(
        getVehicleTrackingId(vehicle) ||
        vehicle.id ||
        vehicle.vehicleId ||
        vehicle.vehicle_id ||
        vehicle.VehicleId ||
        vehicle.name ||
        vehicle.vehicle_name ||
        vehicle.VehicleNumber ||
        vehicle.registrationNumber ||
        vehicle.registration_number ||
        fallbackId
      );
    },
    [getVehicleTrackingId],
  );

  const vehiclesStopHereCount = useMemo(() => {
    if (!stopData?.routes?.length) return 0;

    return new Set(
      stopData.routes.flatMap((route, routeIndex) =>
        (route?.vehicles || []).map((vehicle, vehicleIndex) =>
          getVehicleCountId(vehicle, `${routeIndex}-${vehicleIndex}`),
        ),
      )
        .filter(Boolean),
    ).size;
  }, [getVehicleCountId, stopData]);

  // Helper functions
  const normalizeDeviceData = useCallback((data) => {
    const latitude = data.latitude ?? data.Latitude;
    const longitude = data.longitude ?? data.Longitude;
    const deviceId = data.deviceId ?? data.device_id ?? data.DeviceId;

    if (deviceId && latitude != null && longitude != null) {
      return { ...data, deviceId, latitude, longitude };
    }

    return {
      ...data,
      deviceId,
      latitude,
      longitude,
      timestamp: new Date().toISOString(),
      rawData: data.RawData || "",
    };
  }, []);

  const getBackgroundColor = (deviceId) => {
    const index = Math.abs(hashString(deviceId)) % colors.length;
    return colors[index];
  };

  const hashString = (str) => {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      hash = str.charCodeAt(i) + ((hash << 5) - hash);
      hash = hash & hash;
    }
    return hash;
  };

  const calculateHeading = useCallback((prev, current) => {
    if (!prev || !current) return null;
    return window.google.maps.geometry.spherical.computeHeading(
      new window.google.maps.LatLng(prev.lat, prev.lng),
      new window.google.maps.LatLng(current.lat, current.lng),
    );
  }, []);

  const snapToRoute = useCallback((routeId, position) => {
    const routePath = routePathsRef.current[routeId];
    if (!routePath) return position;
    const point = new window.google.maps.LatLng(position.lat, position.lng);
    let closestPoint = point;
    let minDistance = Infinity;
    for (let i = 0; i < routePath.length - 1; i++) {
      const start = routePath[i];
      const end = routePath[i + 1];
      const projectedPoint = window.google.maps.geometry.spherical.interpolate(
        start,
        end,
        0.5,
      );
      const distance =
        window.google.maps.geometry.spherical.computeDistanceBetween(
          point,
          projectedPoint,
        );
      if (distance < minDistance) {
        closestPoint = projectedPoint;
        minDistance = distance;
      }
    }
    return { lat: closestPoint.lat(), lng: closestPoint.lng() };
  }, []);

  const formatTime = (seconds) => {
    if (!seconds || isNaN(seconds) || seconds <= 0) return "0";
    if (seconds < 60) return `${seconds} sec`;
    if (seconds < 3600) {
      const minutes = Math.floor(seconds / 60);
      return `${minutes} min`;
    }
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    return `${hours} hr ${minutes > 0 ? `${minutes} min` : ""}`;
  };

  const getOrderedRouteCoordinates = useCallback((route) => {
    return [...(route?.routeCoordinates || [])].sort(
      (a, b) => (a.order ?? 0) - (b.order ?? 0),
    );
  }, []);

  const getStopIndexInRoute = useCallback(
    (route, activeStopData) => {
      const routeCoordinates = getOrderedRouteCoordinates(route);

      return routeCoordinates.findIndex((coord) => {
        if (
          activeStopData?.stopId &&
          coord?.stopId &&
          coord.stopId === activeStopData.stopId
        ) {
          return true;
        }

        return (
          parseFloat(coord?.latitude) === parseFloat(activeStopData?.latitude) &&
          parseFloat(coord?.longitude) === parseFloat(activeStopData?.longitude)
        );
      });
    },
    [getOrderedRouteCoordinates],
  );

  const getNearestRouteCoordinateIndex = useCallback(
    (route, position) => {
      const routeCoordinates = getOrderedRouteCoordinates(route);
      if (!routeCoordinates.length || !position) return -1;

      let nearestIndex = -1;
      let nearestDistance = Infinity;

      routeCoordinates.forEach((coord, index) => {
        const coordLat = parseFloat(coord?.latitude);
        const coordLng = parseFloat(coord?.longitude);

        if (Number.isNaN(coordLat) || Number.isNaN(coordLng)) return;

        const distance =
          window.google.maps.geometry.spherical.computeDistanceBetween(
            new window.google.maps.LatLng(position.lat, position.lng),
            new window.google.maps.LatLng(coordLat, coordLng),
          );

        if (distance < nearestDistance) {
          nearestDistance = distance;
          nearestIndex = index;
        }
      });

      return nearestIndex;
    },
    [getOrderedRouteCoordinates],
  );

  const isStopAheadForVehicle = useCallback(
    (route, activeStopData, position, direction) => {
      if (
        !route ||
        !activeStopData ||
        !position ||
        !direction ||
        direction === "unknown"
      ) {
        return true;
      }

      const stopIndex = getStopIndexInRoute(route, activeStopData);
      const vehicleIndex = getNearestRouteCoordinateIndex(route, position);

      if (stopIndex < 0 || vehicleIndex < 0) return true;

      if (direction === "source_to_dest") {
        return stopIndex >= vehicleIndex;
      }

      if (direction === "dest_to_source") {
        return stopIndex <= vehicleIndex;
      }

      return true;
    },
    [getNearestRouteCoordinateIndex, getStopIndexInRoute],
  );

  const formatDistance = useCallback((distance) => {
    const numericDistance = parseFloat(distance);
    if (Number.isNaN(numericDistance) || numericDistance < 0) return "---";
    return `${numericDistance.toFixed(1)} km`;
  }, []);

  // Build deviceId ΓåÆ vehicle name map from stopData routes
  const vehicleNameMap = useMemo(() => {
    const map = {};
    if (!stopData) return map;
    stopData.routes.forEach((route) => {
      (route.vehicles || []).forEach((v) => {
        const trackingId = getVehicleTrackingId(v);
        if (trackingId) {
          map[trackingId] =
            v.name || v.vehicle_name || v.VehicleNumber || trackingId;
        }
      });
    });
    return map;
  }, [getVehicleTrackingId, stopData]);

  // Fetch vehicle directions from Redis for SSE vehicles
  useEffect(() => {
    if (!vehicleIds.length) return;
    const fetchDirections = async () => {
      const dirs = {};
      await Promise.all(
        vehicleIds.map(async (deviceId) => {
          const dir = await getVehicleDirection(deviceId);
          if (dir) dirs[deviceId] = dir;
        }),
      );
      setVehicleDirections((prev) => ({ ...prev, ...dirs }));
    };
    fetchDirections();
    const intervalId = setInterval(fetchDirections, 30000);
    return () => clearInterval(intervalId);
  }, [vehicleIds]);

  // Fetch user permissions
  useEffect(() => {
    const user = JSON.parse(localStorage.getItem("userState"));
    if (user) {
      setUserState(user);
      const mapPermission = user.features.some(
        (feature) =>
          feature.api_path === "/api/stop/organization" &&
          feature.action === "GET",
      );
      setHasMapPermission(mapPermission);
    }
  }, []);

  const getDeviceIdsFromRoutes = useCallback((routes = []) => {
    return [
      ...new Set(
        routes
          .flatMap((route) => route?.vehicles || [])
          .map((vehicle) => getVehicleTrackingId(vehicle))
          .filter(Boolean),
      ),
    ];
  }, [getVehicleTrackingId]);

  const getStopVehicleStatusCounts = useCallback(
    (organizationVehicles = [], selectedDeviceIds = []) => {
      const vehicles = Array.isArray(organizationVehicles)
        ? organizationVehicles
        : Array.isArray(organizationVehicles?.data)
        ? organizationVehicles.data
        : [];
      const selectedIds = new Set(selectedDeviceIds.map(normalizeId).filter(Boolean));
      const countedIds = new Set();

      const counts = vehicles.reduce(
        (acc, vehicle) => {
          const deviceId = getVehicleTrackingId(vehicle);
          if (!deviceId || !selectedIds.has(deviceId) || countedIds.has(deviceId)) {
            return acc;
          }

          countedIds.add(deviceId);
          const status = String(
            vehicle?.status ||
              vehicle?.vehicleStatus ||
              vehicle?.deadStatus ||
              vehicle?.state ||
              "",
          ).trim().toLowerCase();
          if (isActiveVehicleStatus(status)) {
            acc.active += 1;
          } else {
            acc.inactive += 1;
          }
          return acc;
        },
        { active: 0, inactive: 0 },
      );

      counts.inactive += Math.max(0, selectedIds.size - countedIds.size);
      return counts;
    },
    [getVehicleTrackingId],
  );

  const getRoutesForSelectedStop = useCallback(() => {
    const routes = selectedStopData?.routes || [];
    const assignedRoutes = selectedStopData?.assignedRoutes || [];
    const routesHaveVehicles = routes.some(
      (route) => Array.isArray(route?.vehicles) && route.vehicles.length > 0,
    );

    return routesHaveVehicles ? routes : assignedRoutes;
  }, [selectedStopData]);

  // Set stop data on initial load, then enrich with full vehicle data from API
  useEffect(() => {
    if (!selectedStopData) {
      setError("No stop data available");
      return;
    }

    // Reset all state
    setError(null);
    setAllStopData(
      localStopsData?.length ? localStopsData : [selectedStopData],
    );
    setDirections([]);
    setIsInitialized(false);
    setVehicleData({});
    setVehicleHistory({});
    setStoppedVehicles({});
    setStopAnalytics({
      stopsVisited: [],
      totalStopsVisited: 0,
      activeInactive: [],
      loading: false,
      error: null,
    });
    vehicleHistoryRef.current = {};
    stoppedVehiclesRef.current = {};
    routePathsRef.current = {};
    setIsInitialVehicleLoadComplete(false);
    setVehicleIds([]);

    // Set initial stopData immediately so the loading screen renders
    setStopData(selectedStopData);
    stopDataRef.current = selectedStopData;

    const routesToProcess = getRoutesForSelectedStop();

    if (schoolMonitor) {
      const normalizedStopData = { ...selectedStopData, routes: routesToProcess };
      const deviceIds = getDeviceIdsFromRoutes(routesToProcess);

      setStopData(normalizedStopData);
      stopDataRef.current = normalizedStopData;
      setFilteredRoutes(routesToProcess);
      setFilteredVehicles(
        routesToProcess.flatMap((route) => route?.vehicles || []).filter(Boolean),
      );
      setVehicleIds(deviceIds);
      console.log(
        `[SchoolStopMonitor] ${deviceIds.length} vehicle(s) found for this stop:`,
        deviceIds,
      );
      return;
    }

    // Enrich routes with full vehicle arrays from the API, then set vehicleIds
    getAllRoutes()
      .then((allRoutes) => {
        const enrichedRoutes = routesToProcess.map((stopRoute) => {
          const apiRoute = allRoutes.find((r) => r.id === stopRoute.id);
          return {
            ...stopRoute,
            // Prefer full vehicle list from API; fall back to whatever came with nav-state
            vehicles: apiRoute?.vehicles || stopRoute.vehicles || [],
          };
        });

        const enrichedStopData = { ...selectedStopData, routes: enrichedRoutes };
        setStopData(enrichedStopData);
        stopDataRef.current = enrichedStopData;

        const deviceIds = getDeviceIdsFromRoutes(enrichedRoutes);
        console.log(
          `[StopMonitor] ${deviceIds.length} vehicle(s) found for this stop:`,
          deviceIds,
        );
        setVehicleIds(deviceIds);
      })
      .catch((err) => {
        console.error("[StopMonitor] Failed to enrich vehicle data:", err);
        // Fallback: use whatever vehicles came with the navigation state
        setVehicleIds(getDeviceIdsFromRoutes(routesToProcess));
      });
  }, [
    getDeviceIdsFromRoutes,
    getRoutesForSelectedStop,
    localStopsData,
    schoolMonitor,
    selectedStopData,
  ]);

  useEffect(() => {
    stopDataRef.current = stopData;
  }, [stopData]);

  useEffect(() => {
    const orgId = getOrganizationId();
    if (!orgId || !stopData || vehicleIds.length === 0) {
      setStopChunkProgress(null);
      setStopAnalytics({
        stopsVisited: [],
        totalStopsVisited: 0,
        activeInactive: [],
        loading: false,
        error: null,
      });
      return;
    }

    let isActive = true;
    const rangeError = validateAnalyticsDateRange(
      entryExitDateOption,
      entryExitCustomRange,
      t,
    );

    if (rangeError) {
      setEntryExitDateError(rangeError);
      setStopChunkProgress(null);
      setStopAnalytics({
        stopsVisited: [],
        totalStopsVisited: 0,
        activeInactive: [],
        loading: false,
        error: rangeError,
      });
      return;
    }

    setEntryExitDateError("");
    setStopAnalytics((prev) => ({
      ...prev,
      loading: true,
      error: null,
    }));

    const chunks = createAnalyticsDateChunks(entryExitDateOption, entryExitCustomRange);
    if (!chunks.length) {
      setStopChunkProgress(null);
      setStopAnalytics({
        stopsVisited: [],
        totalStopsVisited: 0,
        activeInactive: [],
        loading: false,
        error: t("Please select a valid date range"),
      });
      return;
    }

    setStopChunkProgress(
      chunks.length > 1
        ? { current: 0, total: chunks.length, percentComplete: 0 }
        : null,
    );
    const loadStopAnalytics = async () => {
      try {
        const organizationVehicles = await reportsApi.getVehiclesByOrganization(orgId);
        const runtimeResponses = [];

        for (const [chunkIndex, chunk] of chunks.entries()) {
          const report = await reportsApi.getRuntimeReport({
            orgId,
            category: "vehicles",
            selectedIds: vehicleIds,
            selectAll: false,
            ...chunk,
          });

          runtimeResponses.push(report);
          if (!isActive) return;

          const mergedReport = mergeReportResponses(runtimeResponses);
          const rows = Array.isArray(mergedReport?.data) ? mergedReport.data : [];
          const stopsVisited = rows
            .map((row, index) => {
              const visits = getStopVisitCountFromRuntimeRow(row, stopData);
              return {
                deviceId: getRuntimeVehicleLabel(
                  row,
                  getReportVehicleKey(row, vehicleIds[index]),
                ),
                visits,
              };
            })
            .filter((item) => item.visits > 0);

          const statusCounts = getStopVehicleStatusCounts(
            organizationVehicles,
            vehicleIds,
          );

          setStopAnalytics({
            stopsVisited,
            totalStopsVisited: stopsVisited.reduce(
              (sum, item) => sum + item.visits,
              0,
            ),
            activeInactive: [
              {
                status: t("Active"),
                statusKey: "ACTIVE",
                count: statusCounts.active,
              },
              {
                status: t("Inactive"),
                statusKey: "INACTIVE",
                count: statusCounts.inactive,
              },
            ],
            loading: false,
            error: null,
          });
          if (chunks.length > 1) {
            setStopChunkProgress({
              current: chunkIndex + 1,
              total: chunks.length,
              percentComplete: Math.round(((chunkIndex + 1) / chunks.length) * 100),
            });
          }
        }
        if (isActive) {
          setStopChunkProgress(null);
        }
      } catch (error) {
        console.error("Failed to load stop visit analytics:", error);
        if (!isActive) return;
        setStopChunkProgress(null);

        setStopAnalytics({
          stopsVisited: [],
          totalStopsVisited: 0,
          activeInactive: [],
          loading: false,
          error: t("Failed to load stop analytics"),
        });
      }
    };

    loadStopAnalytics();

    return () => {
      isActive = false;
    };
  }, [
    stopData,
    vehicleIds,
    getStopVehicleStatusCounts,
    entryExitDateOption,
    entryExitCustomRange.startDate,
    entryExitCustomRange.endDate,
  ]);

  // Manage SSE connection
  useEffect(() => {
    if (
      !stopData ||
      !isInitialized ||
      !isInitialVehicleLoadComplete ||
      vehicleIds.length === 0
    ) {
      console.log("No vehicle IDs, skipping SSE connection");
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
        eventSourceRef.current = null;
        isConnectedRef.current = false;
      }
      return;
    }

    const connectSSE = () => {
      if (isConnectedRef.current) {
        console.log("Already connected, skipping new connection");
        return;
      }

      const query = vehicleIds.map((id) => `vehicleId=${id}`).join("&");
      const sseUrl = `${config.baseURL}/redis/stream?${query}`;
      console.log(`Attempting to connect to SSE URL: ${sseUrl}`);

      if (eventSourceRef.current) {
        console.log("Closing previous SSE connection");
        eventSourceRef.current.close();
        eventSourceRef.current = null;
      }

      const source = new EventSource(sseUrl);
      eventSourceRef.current = source;

      source.onopen = () => {
        console.log("SSE connection opened successfully");
        isConnectedRef.current = true;
      };

      source.onerror = (error) => {
        console.error("SSE connection error:", error);
        if (eventSourceRef.current) {
          eventSourceRef.current.close();
          eventSourceRef.current = null;
        }
        isConnectedRef.current = false;
        if (!reconnectTimeoutRef.current) {
          console.log("Scheduling reconnection in 5 seconds");
          reconnectTimeoutRef.current = setTimeout(() => {
            console.log("Reconnecting SSE...");
            connectSSE();
          }, 5000);
        }
      };

      source.onmessage = (event) => {
        try {
          console.log("Received SSE message:", event.data);
          const data = JSON.parse(event.data);
          handleSSEMessageRef.current?.(data);
        } catch (e) {
          console.error("Error parsing SSE message:", e);
        }
      };
    };

    connectSSE();

    return () => {
      console.log("Component unmounting, cleaning up SSE");
      if (eventSourceRef.current) {
        console.log("Closing SSE connection");
        eventSourceRef.current.close();
        eventSourceRef.current = null;
      }
      if (reconnectTimeoutRef.current) {
        console.log("Clearing reconnect timeout");
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }
      isConnectedRef.current = false;
    };
  }, [vehicleIds, isInitialized, isInitialVehicleLoadComplete, stopData]); // Reconnect only after initial seed data loads

  // Fetch directions ΓÇö guard with !isInitialized so enriched-stopData update doesn't re-trigger
  useEffect(() => {
    if (stopData && isLoaded && !isInitialized) {
      fetchDirections(stopData.routes);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stopData, isLoaded, isInitialized]);

  const fetchDirections = async (routes) => {
    if (!routes?.length) {
      setDirections([]);
      routePathsRef.current = {};
      setIsInitialized(true);
      return;
    }

    const directionsService = new window.google.maps.DirectionsService();
    const directionsPromises = routes.map(
      (route) =>
        new Promise((resolve, reject) => {
          const routeCoordinates = [...(route.routeCoordinates || [])].sort(
            (a, b) => (a.order ?? 0) - (b.order ?? 0),
          );

          if (routeCoordinates.length < 2) {
            reject(new Error(`Route ${route.id} has fewer than 2 coordinates`));
            return;
          }

          const waypoints = routeCoordinates
            .slice(1, -1)
            .map((coord) => ({
              location: new window.google.maps.LatLng(
                parseFloat(coord.latitude),
                parseFloat(coord.longitude),
              ),
              stopover: true,
            }));
          directionsService.route(
            {
              origin: new window.google.maps.LatLng(
                parseFloat(routeCoordinates[0].latitude),
                parseFloat(routeCoordinates[0].longitude),
              ),
              destination: new window.google.maps.LatLng(
                parseFloat(
                  routeCoordinates[routeCoordinates.length - 1].latitude,
                ),
                parseFloat(
                  routeCoordinates[routeCoordinates.length - 1].longitude,
                ),
              ),
              waypoints,
              travelMode: window.google.maps.TravelMode.DRIVING,
            },
            (result, status) => {
              if (status === window.google.maps.DirectionsStatus.OK) {
                routePathsRef.current[route.id] =
                  result.routes[0].overview_path;
                resolve(result);
              } else {
                reject(new Error(`Error fetching directions for route ${route.id}: ${status}`));
              }
            },
          );
        }),
    );

    try {
      const settledResults = await Promise.allSettled(directionsPromises);
      const successfulDirections = settledResults
        .filter((result) => result.status === "fulfilled")
        .map((result) => result.value);
      const failedDirections = settledResults.filter(
        (result) => result.status === "rejected",
      );

      if (failedDirections.length > 0) {
        console.warn(
          "[StopMonitor] Some route directions could not be loaded:",
          failedDirections.map((result) => result.reason?.message || result.reason),
        );
      }

      setDirections(successfulDirections);
      setIsInitialized(true);
    } catch (error) {
      console.error("Error fetching directions:", error);
      setDirections([]);
      setIsInitialized(true);
    }
  };

  const fetchDistanceTimeForVehicle = useCallback(
    async (route, deviceId, activeStopData, position) => {
      const direction = vehicleDirections[deviceId];
      const stopIndex = getStopIndexInRoute(route, activeStopData);
      const orderedRouteCoordinates = getOrderedRouteCoordinates(route);
      const isExtreme =
        stopIndex === 0 ||
        stopIndex === orderedRouteCoordinates.length - 1;

      if (
        !isStopAheadForVehicle(route, activeStopData, position, direction)
      ) {
        return { distance: null, eta: null };
      }

      let disTimeData = { distance: null, eta: null };

      try {
        const disTimeRes = await fetch(`${config.baseURL}/distance/time/stop`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            vehicleId: deviceId,
            longitude: activeStopData.longitude,
            latitude: activeStopData.latitude,
            isExtreamPoint: isExtreme,
            isExtremePoint: isExtreme,
            direction,
          }),
        });

        if (!disTimeRes.ok) {
          throw new Error(`Distance/Time API error: ${disTimeRes.status}`);
        }

        const responseData = await disTimeRes.json();
        disTimeData = {
          distance:
            responseData?.distance != null && responseData.distance !== ""
              ? parseFloat(responseData.distance)
              : null,
          eta:
            responseData?.eta != null && responseData.eta !== ""
              ? parseFloat(responseData.eta)
              : null,
        };
      } catch (error) {
        console.error(`Error fetching distance/ETA for ${deviceId}:`, error);
      }

      return disTimeData;
    },
    [
      getOrderedRouteCoordinates,
      getStopIndexInRoute,
      isStopAheadForVehicle,
      vehicleDirections,
    ],
  );

  const loadInitialVehicleLocations = useCallback(async () => {
    if (!stopData || !isInitialized) {
      return;
    }

    if (vehicleIds.length === 0) {
      setIsInitialVehicleLoadComplete(true);
      return;
    }

    try {
      const initialVehicles = await Promise.all(
        vehicleIds.map(async (deviceId) => {
          const route = stopData.routes.find((r) =>
            r.vehicles.some((v) => getVehicleTrackingId(v) === deviceId),
          );

          if (!route) {
            return null;
          }

          try {
            const response = await fetch(
              `${config.baseURL}/location/latest/${deviceId}`,
            );
            const data = await response.json();

            if (
              (data?.latitude ?? data?.Latitude) == null ||
              (data?.longitude ?? data?.Longitude) == null
            ) {
              return null;
            }

            const normalizedData = normalizeDeviceData(data);
            const position = {
              lat: parseFloat(normalizedData.latitude),
              lng: parseFloat(normalizedData.longitude),
            };

            const previousHistory = vehicleHistoryRef.current[deviceId];
            const currentTime = Date.now();
            const timeDiff = currentTime - (previousHistory?.timestamp || 0);
            let isStopped = false;

            if (previousHistory?.position) {
              const distance =
                window.google.maps.geometry.spherical.computeDistanceBetween(
                  new window.google.maps.LatLng(
                    previousHistory.position.lat,
                    previousHistory.position.lng,
                  ),
                  new window.google.maps.LatLng(position.lat, position.lng),
                );

              isStopped = distance < 1 && timeDiff > 30000;
            }

            const disTimeData = await fetchDistanceTimeForVehicle(
              route,
              deviceId,
              stopData,
              position,
            );
            return {
              deviceId,
              historyEntry: {
                position,
                timestamp: currentTime,
              },
              isStopped,
              vehicle: {
                position,
                routeId: route.routeId,
                speed: parseFloat(
                  normalizedData.speed ?? normalizedData.Speed ?? 0,
                ),
                distance: disTimeData.distance,
                eta: disTimeData.eta,
                timestamp: currentTime,
                heading: calculateHeading(
                  previousHistory?.position,
                  position,
                ),
              },
            };
          } catch (error) {
            console.error(
              `Error fetching initial location for vehicle ${deviceId}:`,
              error,
            );
            return null;
          }
        }),
      );

      const validInitialVehicles = initialVehicles.filter(Boolean);
      const initialVehicleData = {};
      const latestHistory = { ...vehicleHistoryRef.current };
      const latestStoppedVehicles = { ...stoppedVehiclesRef.current };

      validInitialVehicles.forEach(({ deviceId, vehicle, historyEntry, isStopped }) => {
        initialVehicleData[deviceId] = vehicle;
        latestHistory[deviceId] = historyEntry;
        latestStoppedVehicles[deviceId] = isStopped;
      });

      vehicleHistoryRef.current = latestHistory;
      stoppedVehiclesRef.current = latestStoppedVehicles;
      setVehicleData(initialVehicleData);
      setVehicleHistory(latestHistory);
      setStoppedVehicles(latestStoppedVehicles);
    } catch (error) {
      console.error("Error fetching initial vehicle locations:", error);
    } finally {
      setIsInitialVehicleLoadComplete(true);
    }
  }, [
    calculateHeading,
    fetchDistanceTimeForVehicle,
    getVehicleTrackingId,
    isInitialized,
    normalizeDeviceData,
    snapToRoute,
    stopData,
    vehicleIds,
  ]);

  useEffect(() => {
    if (!stopData || !isInitialized) {
      return;
    }

    loadInitialVehicleLocations();
  }, [stopData, isInitialized, loadInitialVehicleLocations]);

  // Handle SSE messages
  const handleSSEMessage = useCallback(
    async (data) => {
      const activeStopData = stopDataRef.current;

      if (data.type && data.type === "connected") {
        console.log("SSE connected:", data);
        return;
      }

      if (
        activeStopData &&
        (data.device_id ?? data.deviceId ?? data.DeviceId) &&
        (data.latitude ?? data.Latitude) != null &&
        (data.longitude ?? data.Longitude) != null
      ) {
        const normalizedData = normalizeDeviceData(data);
        const deviceId = normalizedData.deviceId;
        const position = {
          lat: parseFloat(normalizedData.latitude),
          lng: parseFloat(normalizedData.longitude),
        };

        const route = activeStopData.routes.find((r) =>
          r.vehicles.some((v) => getVehicleTrackingId(v) === deviceId),
        );
        if (!route) {
          console.warn(`No route found for vehicle ${deviceId}`);
          return;
        }

        const newHistory = { ...vehicleHistoryRef.current };
        const prevPosition = vehicleHistoryRef.current[deviceId]?.position;
        const currentTime = Date.now();
        const lastUpdateTime =
          vehicleHistoryRef.current[deviceId]?.timestamp || 0;
        const timeDiff = currentTime - lastUpdateTime;

        const newStoppedVehicles = { ...stoppedVehiclesRef.current };
        if (prevPosition) {
          const distance =
            window.google.maps.geometry.spherical.computeDistanceBetween(
              new window.google.maps.LatLng(prevPosition.lat, prevPosition.lng),
              new window.google.maps.LatLng(position.lat, position.lng),
            );
          newStoppedVehicles[deviceId] = distance < 1 && timeDiff > 30000;
        } else {
          newStoppedVehicles[deviceId] = false;
        }

        newHistory[deviceId] = { position, timestamp: currentTime };
        vehicleHistoryRef.current = newHistory;
        stoppedVehiclesRef.current = newStoppedVehicles;

        const disTimeData = await fetchDistanceTimeForVehicle(
          route,
          deviceId,
          activeStopData,
          position,
        );
        setVehicleData((prev) => ({
          ...prev,
          [deviceId]: {
            position,
            routeId: route.routeId,
            speed: parseFloat(normalizedData.speed ?? normalizedData.Speed ?? 0),
            distance: disTimeData.distance,
            eta: disTimeData.eta,
            timestamp: currentTime,
            heading: calculateHeading(prevPosition, position),
          },
        }));

        setVehicleHistory(newHistory);
        setStoppedVehicles(newStoppedVehicles);
      } else {
        console.warn("Invalid SSE data:", data);
      }
    },
    [
      calculateHeading,
      fetchDistanceTimeForVehicle,
      getVehicleTrackingId,
      normalizeDeviceData,
      snapToRoute,
    ],
  );

  useEffect(() => {
    handleSSEMessageRef.current = handleSSEMessage;
  }, [handleSSEMessage]);

  // Handle screen resize
  useEffect(() => {
    const handleResize = () => setIsLargeScreen(window.innerWidth > 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const onUnmount = useCallback(() => {
    setMap(null);
  }, []);

  const handleApplyEntryExitCustomRange = useCallback(() => {
    const rangeError = validateAnalyticsDateRange(
      "custom",
      draftEntryExitCustomRange,
      t,
    );

    if (rangeError) {
      setEntryExitDateError(rangeError);
      setStopChunkProgress(null);
      setEntryExitChunkProgress(null);
      setStopAnalytics({
        stopsVisited: [],
        totalStopsVisited: 0,
        activeInactive: [],
        loading: false,
        error: rangeError,
      });
      setEntryExitAnalytics({
        vehicleVisits: [],
        visitDurations: [],
        timelineVisits: [],
        loading: false,
        error: rangeError,
      });
      return;
    }

    setEntryExitDateError("");
    setEntryExitCustomRange(draftEntryExitCustomRange);
  }, [draftEntryExitCustomRange, t]);

  // Fetch initial data
  const fetchInitialData = useCallback(async () => {
    if (schoolMonitor) {
      const routes = getRoutesForSelectedStop();
      setFilteredVehicles(
        routes.flatMap((route) => route?.vehicles || []).filter(Boolean),
      );
      setFilteredRoutes(routes);
      setFilteredStops(
        localStopsData?.length ? localStopsData : selectedStopData ? [selectedStopData] : [],
      );
      return;
    }

    try {
      const [vehicles, routes, stops] = await Promise.all([
        getAllVehicles(),
        getAllRoutes(),
        getAllStops(),
      ]);

      // Initialize filtered data with all data

      setFilteredVehicles(vehicles);
      setFilteredRoutes(routes);
      setFilteredStops(stops);
    } catch (error) {
      console.log("Error fetching initial data:", error);
    }
  }, [getRoutesForSelectedStop, localStopsData, schoolMonitor, selectedStopData]);
  useEffect(() => {
    fetchInitialData();
  }, [fetchInitialData]);

  useEffect(() => {
    const orgId = getOrganizationId();
    const stopId = stopData?.id || stopData?.stopId;
    if (!orgId || !stopId) {
      setEntryExitChunkProgress(null);
      return;
    }

    let isActive = true;
    const rangeError = validateAnalyticsDateRange(
      entryExitDateOption,
      entryExitCustomRange,
      t,
    );

    if (rangeError) {
      setEntryExitDateError(rangeError);
      setEntryExitChunkProgress(null);
      setEntryExitAnalytics({
        vehicleVisits: [],
        visitDurations: [],
        timelineVisits: [],
        loading: false,
        error: rangeError,
      });
      return;
    }

    setEntryExitDateError("");
    setEntryExitAnalytics({
      vehicleVisits: [],
      visitDurations: [],
      timelineVisits: [],
      loading: true,
      error: null,
    });

    const chunks = createAnalyticsDateChunks(entryExitDateOption, entryExitCustomRange);
    if (!chunks.length) {
      setEntryExitChunkProgress(null);
      setEntryExitAnalytics({
        vehicleVisits: [],
        visitDurations: [],
        timelineVisits: [],
        loading: false,
        error: t("Please select a valid date range"),
      });
      return;
    }

    setEntryExitChunkProgress(
      chunks.length > 1
        ? { current: 0, total: chunks.length, percentComplete: 0 }
        : null,
    );

    const loadEntryExitAnalytics = async () => {
      try {
        const entryExitResponses = [];
        const timelineRange = getTimelineRange(
          entryExitDateOption,
          entryExitCustomRange,
        );

        for (const [chunkIndex, chunk] of chunks.entries()) {
          const report = await reportsApi.getEntryExitReport({
            orgId,
            category: "stops",
            selectedIds: [stopId],
            selectAll: false,
            ...chunk,
          });

          entryExitResponses.push(report);
          if (!isActive) return;

          const mergedReport = mergeReportResponses(entryExitResponses);
          const rows = Array.isArray(mergedReport?.data) ? mergedReport.data : [];
          const countMap = {};
          const durationMap = {};
          const vehicleLabelMap = {};
          const timelineVisits = [];

          rows.forEach((row, index) => {
            const key = getReportVehicleKey(row, row?.vehicleName || row?.vehicleNumber || `Unknown-${index}`);
            const label = row.vehicleName || row.vehicleNumber || row.deviceName || key || "Unknown";
            vehicleLabelMap[key] = label;
            countMap[key] = (countMap[key] || 0) + 1;

            const duration = getVisitDurationMinutes(row);
            if (duration !== null) {
              durationMap[key] = durationMap[key] || { total: 0, count: 0 };
              durationMap[key].total += duration;
              durationMap[key].count += 1;
            }

            const timelineItem = getEntryExitTimelineItem(
              row,
              index,
              timelineRange.startDate,
              timelineRange.endDate,
            );
            if (timelineItem) {
              timelineVisits.push(timelineItem);
            }
          });

          const vehicleVisits = Object.entries(countMap)
            .map(([vehicleKey, visits]) => ({
              vehicle: vehicleLabelMap[vehicleKey] || vehicleKey || "Unknown",
              vehicleKey,
              visits,
            }))
            .sort((a, b) => b.visits - a.visits || a.vehicle.localeCompare(b.vehicle));

          const visitDurations = Object.entries(durationMap).map(
            ([vehicleKey, durationData]) => ({
              vehicle: vehicleLabelMap[vehicleKey] || vehicleKey || "Unknown",
              vehicleKey,
              duration: Number((durationData.total / durationData.count).toFixed(1)),
            }),
          ).sort((a, b) => b.duration - a.duration || a.vehicle.localeCompare(b.vehicle));

          setEntryExitAnalytics({
            vehicleVisits,
            visitDurations,
            timelineVisits: groupTimelineVisitsByVehicle(
              timelineVisits.sort((a, b) => a.start - b.start),
            )
              .sort(
                (a, b) =>
                  b.segmentCount - a.segmentCount ||
                  a.label.localeCompare(b.label),
              )
              .slice(0, 10),
            loading: false,
            error: null,
          });
          if (chunks.length > 1) {
            setEntryExitChunkProgress({
              current: chunkIndex + 1,
              total: chunks.length,
              percentComplete: Math.round(((chunkIndex + 1) / chunks.length) * 100),
            });
          }
        }
        if (isActive) {
          setEntryExitChunkProgress(null);
        }
      } catch (err) {
        console.error("Failed to load entry/exit analytics:", err);
        if (!isActive) return;
        setEntryExitChunkProgress(null);
        const backendErrorMessage =
          err?.response?.data?.error ||
          err?.response?.data?.message ||
          err?.message;
        const errorMessage = backendErrorMessage || t("Failed to load entry/exit data");
        setEntryExitAnalytics({
          vehicleVisits: [],
          visitDurations: [],
          timelineVisits: [],
          loading: false,
          error: errorMessage,
        });
        if (backendErrorMessage) {
          setEntryExitDateError(backendErrorMessage);
        }
      }
    };

    loadEntryExitAnalytics();

    return () => { isActive = false; };
  }, [
    stopData,
    entryExitDateOption,
    entryExitCustomRange.startDate,
    entryExitCustomRange.endDate,
  ]);


  // ΓöÇΓöÇΓöÇ Stop label collision offsets ΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇ
  const stopLabelOffsets = useMemo(() => {
    if (!map || !allStopData?.length) return {};
    const projection = map.getProjection();
    if (!projection) return {};
    const entries = allStopData.map((s) => ({
      id: s.id,
      lat: parseFloat(s.latitude),
      lng: parseFloat(s.longitude),
      name: s.name,
    }));
    return computeStopLabelOffsets(projection, map.getZoom(), entries, {
      BASE_OFFSET: 30,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map, allStopData, zoom]);

  // ΓöÇΓöÇΓöÇ Vehicle label collision offsets ΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇ
  const vehicleLabelOffsets = useMemo(() => {
    if (!map || !Object.keys(vehicleData).length) return {};
    const projection = map.getProjection();
    if (!projection) return {};
    const entries = Object.entries(vehicleData).map(([deviceId, data]) => {
      const direction = vehicleDirections[deviceId];
      const hasArrow = !!(direction && direction !== "unknown");
      return {
        deviceId,
        lat: data.position.lat,
        lng: data.position.lng,
        name: vehicleNameMap[deviceId] || "",
        hasArrow,
      };
    });
    return computeVehicleLabelOffsets(projection, map.getZoom(), entries);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map, vehicleData, zoom, vehicleDirections]);

  const mapStyles = [
    {
      featureType: "all",
      elementType: "all",
      stylers: [{ visibility: "off" }],
    },
    {
      featureType: "road",
      elementType: "geometry",
      stylers: [{ visibility: "on" }, { color: "black" }],
    },
  ];

  const mapContainerStyle = {
    width: "100%",
    height: "550px",
    border: "1px solid #000000",
    marginTop: "0px",
    borderRadius: "20px",
  };

  const renderMap = ({ embedded = false } = {}) => {
    const center = {
      lat: parseFloat(stopData.latitude),
      lng: parseFloat(stopData.longitude),
    };
    const bounds = new window.google.maps.LatLngBounds();
    bounds.extend(center);
    allStopData?.forEach((stop) => {
      bounds.extend({
        lat: parseFloat(stop.latitude),
        lng: parseFloat(stop.longitude),
      });
    });
    return (
      <Box
        sx={{
          borderRadius: "10px",
          // padding: { xs: "10px", sm: "15px", md: "20px" },
          // backgroundColor: "#FFFFFF",
          // border: "1px solid #A5DCF3",
          // margin: embedded
          //   ? 0
          //   : { xs: "10px 15px", sm: "10px 20px", md: "20px 40px" },
          // boxShadow: "-2px 1px 5px 1px #00000040",
          // maxWidth: "100%",
          // height: embedded ? "100%" : "auto",
        }}
      >
       
        <GoogleMap
          mapContainerStyle={mapContainerStyle}
          center={center}
          onLoad={(mapInstance) => {
            setMap(mapInstance);
            mapInstance.fitBounds(bounds, {
              padding: { top: 50, right: 50, bottom: 50, left: 50 },
            });
            setTimeout(() => mapInstance.setCenter(center), 100);
          }}
          onUnmount={onUnmount}
          onZoomChanged={() => {
            if (map) setZoom(map.getZoom());
          }}
          options={{
            styles: mapStyles,
            zoom: 16.5,
            minZoom: 3,
            maxZoom: 20,
            zoomControl: true,
          }}
        >
          {/* ΓöÇΓöÇ Stop markers with MarkerClusterer and collision-safe labels ΓöÇΓöÇ */}
          <MarkerClusterer
            options={(() => {
              const stopIcon = getIntermediateStopIcon();
              const clusterStyle = stopIcon
                ? {
                  url: stopIcon.url,
                  width: 40,
                  height: 40,
                  textColor: "#000000",
                  textSize: 11,
                  fontWeight: "bold",
                  anchorIcon: [40, 20],
                  anchorText: [-2, 0],
                }
                : {
                  url:
                    "data:image/svg+xml;charset=UTF-8," +
                    encodeURIComponent(
                      `<svg xmlns="http://www.w3.org/2000/svg" width="40" height="40">
                          <circle cx="20" cy="20" r="18" fill="#d32f2f" stroke="#fff" stroke-width="2"/>
                        </svg>`,
                    ),
                  width: 40,
                  height: 40,
                  textColor: "#ffffff",
                  textSize: 11,
                  fontWeight: "bold",
                  anchorIcon: [20, 20],
                  anchorText: [0, 0],
                };
              return {
                styles: [clusterStyle, clusterStyle, clusterStyle],
                gridSize: 20,
                minimumClusterSize: 2,
                zoomOnClick: true,
              };
            })()}
          >
            {(clusterer) =>
              (allStopData || []).map((stop) => {
                const isSelected = stop.stopId === stopData.stopId;
                const offsetPx = stopLabelOffsets[stop.id]?.topPx ?? 52;
                const connectorH = offsetPx - 26;
                return (
                  <React.Fragment key={stop.id}>
                    {(zoom === null || zoom >= 14) && (
                      <OverlayView
                        position={{
                          lat: parseFloat(stop.latitude),
                          lng: parseFloat(stop.longitude),
                        }}
                        mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
                      >
                        <div style={{ position: "relative", left: "0px", top: "0px" }}>
                          {/* Name label */}
                          <div
                            style={{
                              position: "absolute",
                              left: "50%",
                              transform: "translateX(-50%)",
                              bottom: `${offsetPx}px`,
                              backgroundColor: "white",
                              color: isSelected ? "blue" : "black",
                              fontWeight: isSelected ? 600 : 500,
                              padding: "3px 8px",
                              borderRadius: "4px",
                              fontSize: "12px",
                              boxShadow: "0 2px 4px rgba(0,0,0,0.25)",
                              whiteSpace: "nowrap",
                              zIndex: 1001,
                              pointerEvents: "none",
                            }}
                          >
                            {stop.name}
                          </div>
                          {/* Connector line */}
                          {connectorH > 0 && (
                            <svg
                              style={{
                                position: "absolute",
                                left: "50%",
                                transform: "translateX(-50%)",
                                bottom: "26px",
                                pointerEvents: "none",
                                overflow: "visible",
                                zIndex: 1000,
                              }}
                              width="2"
                              height={connectorH}
                            >
                              <line
                                x1="1" y1="0" x2="1" y2={connectorH}
                                stroke="rgba(0,0,0,0.45)"
                                strokeWidth="1.5"
                                strokeDasharray="3 2"
                              />
                            </svg>
                          )}
                        </div>
                      </OverlayView>
                    )}
                    <Marker
                      position={{
                        lat: parseFloat(stop.latitude),
                        lng: parseFloat(stop.longitude),
                      }}
                      title={stop.name}
                      icon={
                        isSelected
                          ? {
                            url: "https://maps.google.com/mapfiles/ms/icons/blue-dot.png",
                            scaledSize: new window.google.maps.Size(40, 40),
                          }
                          : getIntermediateStopIcon() || undefined
                      }
                      clusterer={isSelected ? undefined : clusterer}
                    />
                  </React.Fragment>
                );
              })
            }
          </MarkerClusterer>

          {/* ΓöÇΓöÇ Route polylines hidden; showing proximity-based vehicles only ΓöÇΓöÇ */}

          {/* ΓöÇΓöÇ 100m monitoring circle around the selected stop ΓöÇΓöÇ */}
          <Circle
            center={{
              lat: parseFloat(stopData.latitude),
              lng: parseFloat(stopData.longitude),
            }}
            radius={100}
            options={{
              strokeColor: "#20A8E0",
              strokeOpacity: 0.9,
              strokeWeight: 2,
              fillColor: "#20A8E0",
              fillOpacity: 0.12,
              zIndex: 1,
            }}
          />

          {/* ΓöÇΓöÇ Vehicle markers: only those within 200m of the stop ΓöÇΓöÇ */}
          {Object.entries(vehicleData)
            .filter(([, data]) => {
              if (!data?.position) return false;
              const stopLatLng = new window.google.maps.LatLng(
                parseFloat(stopData.latitude),
                parseFloat(stopData.longitude),
              );
              const vehicleLatLng = new window.google.maps.LatLng(
                data.position.lat,
                data.position.lng,
              );
              const dist =
                window.google.maps.geometry.spherical.computeDistanceBetween(
                  stopLatLng,
                  vehicleLatLng,
                );
              return dist <= 1000; // show vehicles within 1 km of the stop
            })
            .map(([deviceId, data]) => {
              const direction = vehicleDirections[deviceId];
              const vehicleIcon = getCustomVehicleIcon(
                deviceId,
                data.heading || 0,
                direction,
                zoom,
              );
              const vehicleName = vehicleNameMap[deviceId] || deviceId;
              const status = vehicleStatuses[deviceId];
              const topPx = vehicleLabelOffsets[deviceId]?.topPx ?? 10;
              const connectorH = topPx;
              const isHovered = hoveredVehicle === deviceId;

              // Build tooltip info
              const vehicleRoute = stopData.routes.find((r) =>
                r.vehicles.some((v) => getVehicleTrackingId(v) === deviceId),
              );
              const vehicleObj = vehicleRoute?.vehicles?.find(
                (v) => getVehicleTrackingId(v) === deviceId,
              );
              const driverName =
                vehicleObj?.driver_name ||
                vehicleObj?.driverName ||
                vehicleObj?.driver ||
                "N/A";
              const routeName = vehicleRoute?.name || vehicleRoute?.routeId || "N/A";

              return (
                <React.Fragment key={deviceId}>
                  <OverlayView
                    position={data.position}
                    mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
                  >
                    <div
                      style={{
                        position: "relative",
                        top: "13px",
                        left: "0px",
                        width: "2px",
                        height: "2px",
                      }}
                    >
                      {(zoom === null || zoom >= 14) && (
                        <>
                          {/* Connector */}
                          {connectorH > 4 && (
                            <svg
                              style={{
                                position: "absolute",
                                left: "50%",
                                transform: "translateX(-50%)",
                                top: "0px",
                                pointerEvents: "none",
                                overflow: "visible",
                                zIndex: 999,
                              }}
                              width="2"
                              height={connectorH}
                            >
                              <line
                                x1="1" y1="0" x2="1" y2={connectorH}
                                stroke="black"
                                strokeWidth="1.8"
                                strokeDasharray="3 2"
                              />
                            </svg>
                          )}
                          {/* Vehicle name label */}
                          <div
                            style={{
                              position: "absolute",
                              top: vehicleLabelOffsets[deviceId]?.top ?? "10px",
                              left: vehicleLabelOffsets[deviceId]?.left ?? "50%",
                              transform:
                                vehicleLabelOffsets[deviceId]?.transform ??
                                "translateX(-50%)",
                              color: status === "INACTIVE" ? "#000" : "#fff",
                              fontSize: "8px",
                              fontWeight: 600,
                              fontFamily: "Arial, sans-serif",
                              lineHeight: "12px",
                              padding: "6px",
                              textAlign: "center",
                              whiteSpace: "nowrap",
                              backgroundColor:
                                status === "DEAD" || status === "INACTIVE"
                                  ? "#F9E719"
                                  : "#008C23",
                              borderRadius: "10px",
                              zIndex: 1000,
                              border: "1.5px solid #008C23",
                            }}
                          >
                            {vehicleName}
                          </div>

                        </>
                      )}
                    </div>
                  </OverlayView>
                  <Marker
                    position={data.position}
                    title={vehicleName}
                    icon={vehicleIcon || undefined}
                  />
                </React.Fragment>
              );
            })}
        </GoogleMap>
      </Box>
    );
  };

  const renderTable = ({ embedded = false } = {}) => {
    let sortedVehicles = Object.entries(vehicleData).sort((a, b) => {
      const distA = parseFloat(a[1].distance);
      const distB = parseFloat(b[1].distance);
      if (isNaN(distA)) return 1;
      if (isNaN(distB)) return -1;
      return distA - distB;
    });
    return (
      <>
        <Box
          sx={{
            overflowX: "auto",
            borderRadius: "10px",
            padding: "20px",
            backgroundColor: "#FFFFFF",
            border: "1px solid #A5DCF3",
            margin: embedded
              ? 0
              : { xs: "10px 15px", sm: "10px 20px", md: "10px 40px" },
            boxShadow: "-2px 1px 5px 1px #00000040",
            height: embedded ? "100%" : "auto",
          }}
        >
          <Table
            style={{
              width: "100%",
              marginTop: "20px",
              borderCollapse: "collapse",
            }}
          >
            <TableHead>
              <TableRow
                style={{ backgroundColor: "#647490", borderRadius: "8px" }}
              >
                <StyledTableCell
                  style={{
                    padding: "8px",
                    textAlign: "left",
                    paddingLeft: "24px",
                    borderTopLeftRadius: "8px",
                    borderBottomLeftRadius: "8px",
                  }}
                >
                  {t("Vehicle")}
                </StyledTableCell>
                <StyledTableCell
                  style={{
                    padding: "8px",
                    textAlign: "left",
                  }}
                >
                  {t("Route")}
                </StyledTableCell>
                <StyledTableCell
                  style={{
                    padding: "8px",
                    textAlign: "left",
                  }}
                >
                  {t("Distance")}
                </StyledTableCell>
                <StyledTableCell
                  style={{
                    padding: "8px",
                    textAlign: "left",
                  }}
                >
                  {t("ETA")}
                </StyledTableCell>
                <StyledTableCell
                  style={{
                    textAlign: "right",
                    padding: "8px",
                    paddingRight: "24px",
                    borderTopRightRadius: "8px",
                    borderBottomRightRadius: "8px",
                  }}
                >
                  {t("Speed")}(km/h)
                </StyledTableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedVehicles.length > 0 ? (
                sortedVehicles.map(([deviceId, data]) => {
                  const route = stopData?.routes?.find((candidateRoute) =>
                    (candidateRoute?.vehicles || []).some(
                      (vehicle) => getVehicleTrackingId(vehicle) === deviceId,
                    ),
                  );
                  const stopIsAhead = isStopAheadForVehicle(
                    route,
                    stopData,
                    data.position,
                    vehicleDirections[deviceId],
                  );

                  return (
                    <TableRow key={deviceId}>
                      <TableCell
                        style={{
                          paddingLeft: "24px",
                          fontWeight: "400",
                          fontSize: "16px",
                        }}
                      >
                        {vehicleNameMap[deviceId] || deviceId || "---"}
                      </TableCell>
                      <TableCell style={{ padding: "8px" }}>
                        {route?.name || route?.routeName || data.routeId || "---"}
                      </TableCell>
                      <TableCell style={{ padding: "8px" }}>
                        {stopIsAhead ? formatDistance(data.distance) : "---"}
                      </TableCell>
                      <TableCell style={{ padding: "8px" }}>
                        {stopIsAhead && data.eta != null
                          ? formatTime(data.eta)
                          : "---"}
                      </TableCell>
                      <TableCell style={{ textAlign: "center" }}>
                        {data.speed || "---"} km/h
                      </TableCell>
                    </TableRow>
                  );
                })
              ) : (
                <TableRow>
                  <TableCell colSpan={5} style={{ textAlign: "center" }}>
                    {t("No vehicles currently available")}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </Box>
      </>
    );
  };

  const renderRouteTable = () => (
    <>
      <div
        style={{
          paddingTop: "32px",
          display: "flex",
          marginBottom: "32px",
          flexDirection: isLargeScreen ? "row" : "column",
        }}
      >
        <div style={{ flex: 3 }}>{renderStopDetails()}</div>
        <div style={{ flex: 7, marginRight: "20px" }}>
          <div style={{ flex: 1, marginRight: "20px" }}>
            <div
              className="form_header form_title"
              style={{
                marginBottom: "0px",
                height: "80px",
                borderRadius: "8px",
                backgroundColor: "#647490",
                fontSize: "28px",
              }}
            >
              {t("Assigned Routes")}
            </div>
            <div>
              <table
                style={{
                  width: "100%",
                  borderCollapse: "collapse",
                  marginTop: "-20px",
                }}
              >
                <thead>
                  <tr style={{ backgroundColor: "#f2f2f2" }}>
                    <th
                      style={{
                        padding: "24px",
                        textAlign: "left",
                        background: "rgba(32, 168, 224, 1)",
                        color: "white",
                        fontWeight: "600",
                      }}
                    >
                      {t("Route ID")}
                    </th>
                    <th
                      style={{
                        padding: "8px",
                        background: "rgba(32, 168, 224, 1)",
                        textAlign: "right",
                        color: "white",
                        paddingRight: "24px",
                        fontWeight: "600",
                      }}
                    >
                      {t("Route Name")}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {stopData.routes.map((route) => (
                    <tr key={route.routeId}>
                      <td style={{ padding: "8px", paddingLeft: "24px" }}>
                        {route.routeId}
                      </td>
                      <td style={{ paddingRight: "20px", textAlign: "right" }}>
                        {route.name}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <Pagination
              itemsPerPage={10}
              currentPage={1}
              onPageChange={(page) => console.log(page)}
            />
          </div>
        </div>
      </div>
    </>
  );

  const renderStopDetails = () => (
    <div style={{ margin: "0px 24px" }}>
      <div
        className="form_header form_title"
        style={{
          marginBottom: "0px",
          height: "50px",
          borderRadius: "8px",
          backgroundColor: "#647490",
          fontSize: "28px",
        }}
      >
        {t("Stop Details")}
      </div>
      <div style={{ marginTop: "32px", lineHeight: "40px" }}>
        <span style={{ fontWeight: "600" }}>{t("Stop ID")}:</span>
        <span style={{ fontWeight: "400" }}> {stopData.stopId}</span>
        <br />
        <span style={{ fontWeight: "600" }}>{t("Total Routes")}:</span>
        <span style={{ fontWeight: "400" }}> {stopData.routes.length}</span>
        <br />
        <span style={{ fontWeight: "600" }}>{t("Stop Name")}:</span>
        <span style={{ fontWeight: "400" }}> {stopData.name}</span>
        <br />
        <span style={{ fontWeight: "600" }}>{t("Longitude")}:</span>
        <span style={{ fontWeight: "400" }}> {stopData.longitude}</span>
        <br />
        <span style={{ fontWeight: "600" }}>{t("Latitude")}:</span>
        <span style={{ fontWeight: "400" }}> {stopData.latitude}</span>
      </div>
      <div
        style={{
          display: "flex",
          gap: "2px",
          flexDirection: isLargeScreen ? "row" : "column",
        }}
      >
        <div
          className="customButton_add"
          style={{
            background: "#F9E719",
            color: "black",
            fontSize: "19px",
            whiteSpace: isLargeScreen ? "nowrap" : "normal",
          }}
        >
          {t("View Stop Schedules")}
        </div>
        <div
          className="customButton_add"
          onClick={() =>
            navigate(`/track/screen/${selectedStopData.id}`, {
              state: { selectedStopData, localStopsData },
            })
          }
          style={{
            marginLeft: isLargeScreen ? "10px" : "0px",
            background: "rgba(32, 168, 224, 1)",
            color: "white",
            whiteSpace: isLargeScreen ? "nowrap" : "normal",
            cursor: "pointer",
          }}
        >
          {t("Stop View")}
        </div>
      </div>
    </div>
  );

  if (loadError) {
    return (
      <>
        {!schoolMonitor && (
          <>
            <TitleBar />
            <NavMenu />
          </>
        )}
        <div>Map cannot be loaded right now, sorry.</div>
      </>
    );
  }
  if (error) {
    return (
      <>
        {!schoolMonitor && (
          <>
            <TitleBar />
            <NavMenu />
          </>
        )}
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "100vh",
            textAlign: "center",
          }}
        >
          <p>{error}</p>
        </div>
      </>
    );
  }

  if (!isLoaded || !stopData || !isInitialized || !isInitialVehicleLoadComplete) {
    return (
      <LoadingScreen
        t={t}
        navigate={navigate}
        selectedStopData={selectedStopData}
        localStopsData={localStopsData}
        isSchoolMonitor={schoolMonitor}
      />
    );
  }

  const handleViewStop = () => {
    navigate(
      `/track/screen/${selectedStopData.id}?org=${selectedStopData.organizationId}&InfoSide=right`,
      {
        state: { selectedStopData, localStopsData },
      },
    );
  };

  const handleStopSchedules = () => {
    navigate("/stop/Schedule/Details", {
      state: { id: selectedStopData.id },
    });
  };

  const orgName = JSON.parse(localStorage.getItem("userState"))?.organization
    ?.name;



  console.log("SchoolStopMonitor - selectedStopData:", selectedStopData);
  console.log("SchoolStopMonitor - localStopsData:", localStopsData);


  return (
    <>
      {!schoolMonitor && (
        <>
          <TitleBar />
          <NavMenu />
        </>
      )}
      <MonitorSubtitle
        title={t(schoolMonitor ? "Monitor School Stop" : "Monitor Stop")}
        path={t(
          `${orgName || "organization"} > ${schoolMonitor ? "School Stops" : "Stops"
          } > ${stopData?.name || "Monitor Stop"
          } > Monitor`,
        )}
        track={schoolMonitor ? null : "Stop View"}
        add={schoolMonitor ? null : "View Schedules"}
        onTrackClick={schoolMonitor ? undefined : handleViewStop}
        onAddClick={schoolMonitor ? undefined : handleStopSchedules}
      />

      <Box
        sx={{
          borderRadius: "10px",
          display: "flex",
          flexDirection: { xs: "column", sm: "row" }, // Stack vertically on small screens
          justifyContent: "space-between",
          padding: { xs: "15px", sm: "15px 30px", md: "20px 60px" }, // Responsive padding
          backgroundColor: "#FFFFFF",
          border: "1px solid #A5DCF3",
          margin: { xs: "0px 15px", sm: "0px 20px", md: "0px 40px" }, // Responsive margins
          boxShadow: "-2px 1px 5px 1px #00000040",
          gap: { xs: 2, sm: 0 }, // Add gap between items on small screens
          overflowX: { xs: "hidden", sm: "auto" },
        }}
      >
        {/* Stop Name Section */}
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            flex: 1,
            mb: { xs: 2, sm: 0 }, // Bottom margin only on small screens
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
            <Typography
              variant="subtitle2"
              sx={{
                fontFamily: "Roboto",
                fontWeight: 500,
                fontSize: { xs: "16px", md: "20px" }, // Responsive font size
                lineHeight: "100%",
                letterSpacing: "0%",
                textAlign: "center",
                color: "#183059",
              }}
            >
              {t("Stop Name")}
            </Typography>
          </Box>
          <Typography
            variant="body1"
            sx={{
              fontFamily: "Roboto",
              fontWeight: 500,
              fontSize: { xs: "18px", md: "24px" }, // Responsive font size
              lineHeight: "100%",
              letterSpacing: "0%",
              textAlign: "center",
              overflow: "hidden",
              textOverflow: "ellipsis",
              maxWidth: "100%",
            }}
          >
            {stopData.name}
          </Typography>
        </Box>

        {/* Divider - horizontal on small screens, vertical on larger */}
        <Divider
          orientation={{ xs: "horizontal", sm: "vertical" }}
          flexItem
          sx={{
            mx: { sm: 2 },
            my: { xs: 2, sm: 0 }, // Vertical margin on small screens
            height: { xs: "1px", sm: "60px" }, // Height for horizontal vs vertical
            width: { xs: "100%", sm: "auto" }, // Full width on small screens
            alignSelf: "center",
            border: "1px solid #183059",
          }}
        />

        {/* Stop ID Section */}
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            flex: 1,
            mb: { xs: 2, sm: 0 }, // Bottom margin only on small screens
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
            <Typography
              variant="subtitle2"
              sx={{
                fontFamily: "Roboto",
                fontWeight: 500,
                fontSize: { xs: "16px", md: "20px" }, // Responsive font size
                lineHeight: "100%",
                letterSpacing: "0%",
                textAlign: "center",
                color: "#183059",
              }}
            >
              {t("Stop ID")}
            </Typography>
          </Box>
          <Typography
            variant="body1"
            sx={{
              fontFamily: "Roboto",
              fontWeight: 500,
              fontSize: { xs: "18px", md: "24px" }, // Responsive font size
              lineHeight: "100%",
              letterSpacing: "0%",
              textAlign: "center",
            }}
          >
            {stopData.stopId}
          </Typography>
        </Box>

        {/* Second divider */}
        <Divider
          orientation={{ xs: "horizontal", sm: "vertical" }}
          flexItem
          sx={{
            mx: { sm: 2 },
            my: { xs: 2, sm: 0 }, // Vertical margin on small screens
            height: { xs: "1px", sm: "60px" }, // Height for horizontal vs vertical
            width: { xs: "100%", sm: "auto" }, // Full width on small screens
            alignSelf: "center",
            border: "1px solid #183059",
          }}
        />

        {/* Connected Routes Section */}
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            flex: 1,
            mb: { xs: 2, sm: 0 }, // Bottom margin only on small screens
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
            <Typography
              variant="subtitle2"
              sx={{
                fontFamily: "Roboto",
                fontWeight: 500,
                fontSize: { xs: "16px", md: "20px" }, // Responsive font size
                lineHeight: "100%",
                letterSpacing: "0%",
                textAlign: "center",
                color: "#183059",
              }}
            >
              {t("Connected Routes")}
            </Typography>
          </Box>
          <Box sx={{ textAlign: "center" }}>
            <Typography
              variant="body1"
              sx={{
                fontFamily: "Roboto",
                fontWeight: 500,
                fontSize: { xs: "18px", md: "24px" }, // Responsive font size
                lineHeight: "100%",
                letterSpacing: "0%",
                textAlign: "center",
              }}
            >
              {stopData.routes.length}
            </Typography>
          </Box>
        </Box>

        {/* Third divider */}
        <Divider
          orientation={{ xs: "horizontal", sm: "vertical" }}
          flexItem
          sx={{
            mx: { sm: 2 },
            my: { xs: 2, sm: 0 }, // Vertical margin on small screens
            height: { xs: "1px", sm: "60px" }, // Height for horizontal vs vertical
            width: { xs: "100%", sm: "auto" }, // Full width on small screens
            alignSelf: "center",
            border: "1px solid #183059",
          }}
        />

        {/* Vehicles Stop Here Section */}
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            flex: 1,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
            <Typography
              variant="subtitle2"
              sx={{
                fontFamily: "Roboto",
                fontWeight: 500,
                fontSize: { xs: "16px", md: "20px" }, // Responsive font size
                lineHeight: "100%",
                letterSpacing: "0%",
                textAlign: "center",
                color: "#183059",
              }}
            >
              {t("Vehicles Stop Here")}
            </Typography>
          </Box>
          <Box
            sx={{
              padding: { xs: "4px 12px", md: "4px 16px" },
              borderRadius: "4px",
              display: "inline-block",
              textAlign: "center",
            }}
          >
            <Typography
              variant="body1"
              sx={{
                fontFamily: "Roboto",
                fontWeight: 500,
                fontSize: { xs: "18px", md: "24px" }, // Responsive font size
                lineHeight: "100%",
                letterSpacing: "0%",
                textAlign: "center",
              }}
            >
              {vehiclesStopHereCount || "---"}
            </Typography>
          </Box>
        </Box>
        <StopHeaderDeviceItems stop={stopData} t={t} />
      </Box>

      {/* {renderRouteTable()} */}
      <Box
        sx={{
          // display: "flex",
          // flexDirection: { xs: "column", lg: "row" },
          gap: 2,
          padding: { xs: "10px 15px", sm: "10px 20px", md: "20px 40px" },
          alignItems: "stretch",
          margin: { xs: "10px 15px", sm: "10px 20px", md: "20px 40px" },
          backgroundColor: "#FFFFFF",
          border: "1px solid #A5DCF3",
          boxShadow: "-2px 1px 5px 1px #00000040",
          maxWidth: "100%",
          height: "auto",
          borderRadius: "10px",        }}
      >
         <Box
          sx={{
            minHeight: "48px",
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            justifyContent: { xs: "center", sm: "left" },
            alignItems: { xs: "flex-start", sm: "flex-start" },
            gap: { xs: 1, sm: 0 },
            padding: { xs: "8px 12px", sm: "10px 14px", md: "12px 16px" },
            background: "#E8EAEE",
            boxShadow: "0px 2px 5px 0px #00000040",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              width: { xs: "100%", sm: "100%" },
            }}
          >
            <Typography
              sx={{
                color: "#414958",
                fontFamily: "Roboto",
                fontWeight: 400,
                fontSize: { xs: "16px", sm: "18px", md: "20px" },
                lineHeight: "100%",
                letterSpacing: "0%",
              }}
            >
              {t("Longitude")}:
            </Typography>
            <Typography
              sx={{
                fontFamily: "Roboto",
                fontWeight: 600,
                fontSize: { xs: "16px", sm: "18px", md: "20px" },
                lineHeight: "100%",
                color: "#183059",
                letterSpacing: "0%",
                ml: 1,
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {stopData?.longitude || "---"}
            </Typography>
          </Box>

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              width: { xs: "100%", sm: "100%" },
            }}
          >
            <Typography
              sx={{
                color: "#414958",
                fontFamily: "Roboto",
                fontWeight: 400,
                fontSize: { xs: "16px", sm: "18px", md: "20px" },
                lineHeight: "100%",
                letterSpacing: "0%",
              }}
            >
              {t("Latitude")}:
            </Typography>
            <Typography
              sx={{
                fontFamily: "Roboto",
                fontWeight: 600,
                fontSize: { xs: "16px", sm: "18px", md: "20px" },
                lineHeight: "100%",
                color: "#183059",
                letterSpacing: "0%",
                ml: 1,
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {stopData.latitude || "---"}
            </Typography>
          </Box>
        </Box>
        <Box sx={{
          display: "flex",
          gap: 2,
          alignItems: "stretch",
          mt: 2,
          flexDirection: { xs: "column", lg: "row" }}}>
        <Box sx={{ flex: "1 1 auto", minWidth: 0 }}>
          {hasMapPermission && renderMap({ embedded: true })}
        </Box>
        <Box
          // sx={{
          //   display: "flex",
          //   flexDirection: { xs: "column", lg: "row" },
          //   gap: 2,
          //   alignItems: "stretch",
          //   margin: { xs: "0 15px 10px", sm: "0 20px 10px", md: "0 15px 20px" },
          //   backgroundColor: "#FFFFFF",
          //   // border: "1px solid #A5DCF3",
          //   // boxShadow: "-2px 1px 5px 1px #00000040",
          //   maxWidth: "100%",
          //   height: "auto",
          //   borderRadius: "10px",
          // }}
            sx={{
              flex: { xs: "1 1 auto", lg: "0 0 520px" },
              minWidth: 0,
              maxHeight: { lg: "650px" },
              overflow: "auto",
            }}
        >
          {renderTable({ embedded: true })}
        </Box>
        </Box>
      </Box>
      <StopAnalyticsPanel
        stopAnalytics={stopAnalytics}
        entryExitAnalytics={entryExitAnalytics}
        dateOption={entryExitDateOption}
        customDateRange={draftEntryExitCustomRange}
        appliedCustomDateRange={entryExitCustomRange}
        stopChunkProgress={stopChunkProgress}
        entryExitChunkProgress={entryExitChunkProgress}
        onDateOptionChange={setEntryExitDateOption}
        onCustomDateRangeChange={setDraftEntryExitCustomRange}
        onApplyCustomDateRange={handleApplyEntryExitCustomRange}
        dateError={entryExitDateError}
        t={t}
      />
      <Footer />
    </>
  );
};

export default AdminStopView;
